/*
 * myPokemonOcr.ts
 *
 * Pokémon GO **individual caught-Pokémon page** OCR — the screen you land on
 * when you tap a Pokémon you OWN (from storage, a raid reward, the buddy
 * screen…), showing that one real Pokémon's CP, nickname, HP, moveset,
 * appraisal IVs and provenance. This is a different page from the Pokédex
 * SPECIES detail page (`pokemonDetailDexOcr.ts`), which shows an index entry
 * and its variant cards — that page is about "have you ever caught one";
 * this page is about "this exact Slaking".
 *
 *   extractMyPokemonPage(dataUrl) -> MyPokemonFrame
 *
 * The frame feeds `myPokemonStore` (the device-first My Pokémon database) via
 * the live-scan hook, which merges partial reads: the page has TWO views the
 * user flips between — the detail view (CP / name / HP / stats / moves /
 * provenance) and the appraisal overlay (team leader + the three IV bars) —
 * and a single frame only ever shows one of them, so a complete record is
 * assembled across frames rather than read in one go.
 *
 * ── What each view gives us ─────────────────────────────────────────────────
 * DETAIL view (top):    CP banner, display name (may be a nickname), current /
 *                       max HP, "LUCKY POKÉMON" banner, user tag pills, gender,
 *                       weight / type icons / height row, stardust + candy
 *                       (whose label names the FAMILY BASE species, not this
 *                       species), caught-date badge.
 * DETAIL view (lower):  the GYMS & RAIDS moveset (fast move first, then
 *                       charged moves, each "Name …… power"), and the
 *                       provenance card (CAUGHT IN THE WILD / OBTAINED IN A
 *                       TRADE / REMOTELY CAUGHT AT A RAID / LOCATION entries
 *                       with values and dates).
 * APPRAISAL overlay:    the three IV BARS (Attack / Defense / HP, 0-15 each —
 *                       bars, not numbers, so they are PIXEL-sampled via
 *                       `sampleIvBarsFromRgba`, never OCR'd), the star seal,
 *                       and the bottom sheet sentence "This <Species> was
 *                       caught on <date> around <place>." — which names the
 *                       SPECIES even when the Pokémon is nicknamed, so it is
 *                       the strongest species signal on the whole page.
 *
 * ── Lineage note (read before extending) ────────────────────────────────────
 * An earlier abandoned effort ("deep scan": detailOcr.ts, frameRouter.ts,
 * screenClassifier.ts and friends) parsed this same page for a review-queue /
 * server-commit pipeline that no longer exists. This module deliberately does
 * NOT import any of it. It shares only the LIVE primitives that the current
 * scanner also uses: `ocrEngine` for text blocks, `ocrPixelOps` for the IV-bar
 * and star-seal pixel samplers, and `ocrRois` for the calibrated rects.
 */

import { classifyFrameAspect } from "./frameGeometry";
import { loadFrameImage } from "./frameImageCache";
import {
  recognizeRegions,
  type NumberBox,
  type OcrRegion,
  type OcrTextBlock,
} from "./ocrEngine";
import { SPECIES_NAMES } from "../data/speciesNames";
import { decodeStyledGlyphs } from "./ivNicknames";
import { ALL_TYPES, typesFor, type PokeType } from "./types";
import { isVariantAvailable } from "./variantAvailability";
import { canonicalMoveName, isKnownMoveName, moveNameKey } from "./moveTypeLookup";
import { isPlausibleHeight, isPlausibleWeight } from "./speciesSize";
import { evolutionChainFor, SPECIES_BASE_STATS } from "./generated/speciesBaseStats.generated";
import { computeCp, computeHp } from "./ivSolver";
import { starsFromIv } from "./myPokemonDerived";
import { looksLikeSpeciesMisread, normalizeSpeciesToken } from "./speciesNameMisread";
export { withinOneEdit } from "./speciesNameMisread";
import { DETAIL_HUD_UPSELL_CTA, HUD_PAGE_STATUS_LINES } from "./hudStatusLines";
import { DEX_SCAN_HUD_LINES } from "./dexScanGuidance";
import type { MyPokemonMaxMovesRead, MyPokemonSizeClass } from "./myPokemonStore";
import { matchMaxMoveName, parseMaxMoveTileLabel, speciesUsesMaxMoves } from "./maxMoves";
import { variantSupportsSpecies } from "./speciesVariants";
import { DETAIL_SPRITE_ROI } from "./ocrRois";
import { classifyShinySprite } from "./myPokemonShiny";
import { classifyShadowAura } from "./myPokemonShadow";
import { CANVAS_READBACK } from "./canvasReadback";

// ── Types ────────────────────────────────────────────────────────────────────

export interface MyPokemonMove {
  /** Cleaned display name, e.g. "Play Rough". */
  name: string;
  /** The power number printed on the row's right edge, or null when unread. */
  power: number | null;
  /** Fast (first row, no energy bar) vs charged. */
  kind: "fast" | "charged";
}

export type ProvenanceKind =
  | "wild"
  | "raid"
  | "trade"
  | "egg"
  | "location"
  | "other";

export interface MyPokemonProvenanceEntry {
  kind: ProvenanceKind;
  /** The printed label, e.g. "CAUGHT IN THE WILD". */
  label: string;
  /** The value under the label — a place or a trainer name — or null. */
  value: string | null;
  /** Raw date string as printed ("11/06/2021"), or null. */
  dateRaw: string | null;
  /** ISO date when unambiguously parseable, else null. */
  dateIso: string | null;
}

export interface MyPokemonIvRead {
  atk: number;
  def: number;
  hp: number;
  /** 0-100, the weakest of the three bar reads. */
  confidence: number;
}

export interface MyPokemonFrame {
  /** True when the frame parses as an individual caught-Pokémon page. */
  isMyPokemonPage: boolean;
  /**
   * 2026-09-03: true when the frame is the STORAGE BOX instead (the grid of
   * caught Pokémon, a CP label under each). Only ever set on a frame that is
   * NOT a caught-Pokémon page; the live scan drops its latched Pokémon at
   * once on it rather than after the miss grace.
   */
  isStorageBox?: boolean;
  /** Which view produced this frame (appraisal = IV overlay visible). */
  view: "detail" | "appraisal";
  /** The name printed above the HP bar — nickname OR species name. */
  displayName: string | null;
  /** National dex id when the name (or appraisal sentence) matched a species. */
  speciesId: number | null;
  /** Canonical species name for `speciesId`, or null. */
  speciesName: string | null;
  /** `displayName` when it did NOT match a species (i.e. a nickname). */
  nickname: string | null;
  cp: number | null;
  hpCurrent: number | null;
  hpMax: number | null;
  gender: "male" | "female" | null;
  weightKg: number | null;
  heightM: number | null;
  /** GO's size-class badge in the weight/height row, when printed. */
  sizeClass: MyPokemonSizeClass;
  /** Validated lowercase type names read from the weight/height row. */
  types: PokeType[];
  stardust: number | null;
  candy: number | null;
  candyXl: number | null;
  /** The species family the candy label names ("SLAKOTH" on a Slaking). */
  candyFamilyName: string | null;
  /** Mega Energy the page states, and the species its label names
   *  ("METAGROSS MEGA ENERGY" on a Metang page). 2026-09-03. */
  megaEnergy: number | null;
  megaEnergySpeciesName: string | null;
  /** Every Mega Energy figure the page shows, X and Y apart (2026-09-03):
   *  a Mewtwo page prints "MEWTWO MEGA ENERGY X" and "... Y" side by side. */
  megaEnergies: MegaEnergyReading[];
  /**
   * The Mega Level circles under each MEGA EVOLVE button (2026-09-03 owner
   * ask): per Mega form, how many are filled (the level) and how far the
   * next one is filled (progress toward the next level). Empty when the
   * page showed no circles.
   */
  megaReads: MegaFormRead[];
  /** The rest period printed beside the Mega badge ("4 DAYS"), as ms left. */
  megaRestMs: number | null;
  /**
   * True when the frame is GO's MEGA LEVEL page instead of a caught-Pokémon
   * page ("Mewtwo's Mega Level": the level banner, the rest period, "N more
   * times" and the form's energy). Set on a frame that is NOT a
   * caught-Pokémon page; the read sits in `megaLevelPage`.
   */
  isMegaLevelPage?: boolean;
  megaLevelPage?: MegaLevelPageRead | null;
  isLucky: boolean;
  /** True when the sprite crop matched the SHINY palette (colour classifier);
   * false when it matched base; absent certainty stays false + a warning. */
  isShiny: boolean;
  /** The purple flame aura around the model (lib/myPokemonShadow); false when unread. */
  isShadow: boolean;
  /** GO prints a "Dynamax" / "Gigantamax" band under the weight-height row on
   * an individual that HAS the ability (a Max Battle catch). The band names
   * exactly one of the two, so these are never both true off one page. */
  isDynamax: boolean;
  isGmax: boolean;
  /**
   * The Max Moves panel (2026-09-04): the pink "Max Moves" ribbon GO prints
   * on an individual that has them, and the three tiles (attack / Guard /
   * Spirit, each "MAX!", "LOCKED" or a level) when the page is scrolled to
   * them. Null when the page shows neither. Optional so hand-built test
   * frames from before the field keep compiling.
   */
  maxMoves?: MyPokemonMaxMovesRead | null;
  /** User tag pills ("GBL", "Aged", ".BLUE" …) — arbitrary user text. */
  tags: string[];
  moves: MyPokemonMove[];
  provenance: MyPokemonProvenanceEntry[];
  /** Raw caught date as printed (badge or appraisal sentence), or null. */
  caughtDateRaw: string | null;
  /** ISO caught date when unambiguous, else null. */
  caughtDateIso: string | null;
  caughtLocation: string | null;
  /**
   * 2026-09-22: the WILD ENCOUNTER banner ("Sprigatito / CP353"), read only
   * on a frame that is NOT a caught-Pokémon page. Optional so hand-built
   * frames keep compiling.
   */
  encounter?: EncounterBannerRead | null;
  /** Pixel-sampled IV bars from the appraisal overlay, or null off it. */
  iv: MyPokemonIvRead | null;
  /** Gold stars on the appraisal seal (0-3), or null when not visible. */
  appraisalStars: number | null;
  /** 0-100 overall read confidence. */
  confidence: number;
  warnings: string[];
  /**
   * One compact line saying WHY an appraisal-looking frame ended without a
   * triple (2026-09-18 owner ask: iOS reporters on 1.9.1 whose frames read
   * fine offline). Set only when the sheet or the labels say the overlay is
   * on screen and `iv` is still null: the label boxes as OCR placed them, or
   * the bottom-left texts when no label was read, and per strip the crop
   * size, the best row's bar-pixel count against the floor it needed and the
   * card-white reference. Diagnostic only; nothing reads it but the log.
   */
  ivDiag?: string;
}

// ── Geometry constants ───────────────────────────────────────────────────────

/**
 * THE WHOLE FRAME, as asked (2026-09-16 owner report). This used to start at
 * y=0.06, the species-detail extractor's status-bar exclusion, on the theory
 * that the band clipped the CP glyph tops ("CP 319" for a CP66 Lechonk); the
 * real cause was our own HUD card (see dropHudEcho). Two things to know:
 *
 *   1. The engine still clamps every region to start at y=0.06
 *      (ocrEngine's clampRegionBelowStatusBar), so the top 6% is NOT read
 *      whatever this says. dropStatusBarChrome below is the safety net for
 *      the day that clamp is lifted.
 *   2. Every block comes back relative to THIS region (the engine remaps
 *      from the clamped one), so mapBlockToFrame is exact. Between 14:05 and
 *      the fix on 2026-09-17 the engine did not remap, every block sat 0.06
 *      too high, and the label-anchored IV bar strips missed the bars: no
 *      live scan read IVs for a day.
 */
const SCAN_ROI: OcrRegion = { x: 0, y: 0, w: 1, h: 1 };

/** Below this y-centre a block may be status-bar chrome (clock, battery, signal). */
const STATUS_BAR_MAX_Y = 0.05;
/** The clock ("8:11", "18:11"), a battery percentage ("35", "100%"), signal glyph runs. */
const STATUS_CHROME_RE = /^(\d{1,2}:\d{2}(\s*[AP]M)?|\d{1,3}\s*%?|[•·.\-_|]+|5G|LTE|4G|3G|E)$/i;

/**
 * Drop the iOS/Android status bar's own text without touching anything GO
 * prints. A block is chrome when it sits in the top band AND reads as a
 * clock, a battery figure or a carrier glyph. "CP66" in the same band (a
 * status-bar-less screenshot) is neither, so it survives.
 */
/**
 * Text only OUR OWN HUD prints: the status lines (one shared list with the
 * hook that pushes them), the pip slot labels, the grid-scan coaching, the
 * iOS "NEXUS DEX" footer. GO never prints any of it.
 */
const escapeRe = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
/** The card's TOP: a status line sits on its first row (after the dot). */
const HUD_STATUS_RE = new RegExp(
  [...HUD_PAGE_STATUS_LINES, ...DEX_SCAN_HUD_LINES]
    .map((l) => escapeRe(l.replace(/…$/, "")))
    .concat(["Reading \\d+\\s*[×x]\\s*\\d+ grid"])
    .join("|"),
  "i",
);
/** The card's BOTTOM: the brand tag in its bottom-left corner (iOS only). */
const HUD_FOOTER_RE = /NEXUS\s*DEX/i;
/** Somewhere inside the card: the league line, the pip slot labels, the CTA. */
const HUD_INNER_RE = new RegExp(
  `Best (GL|UL|ML)\\b|Already up to date|Dex updated|${escapeRe(DETAIL_HUD_UPSELL_CTA)}`,
  "i",
);
/**
 * The card's size as a fraction of the frame is NOT known: the system picks
 * the PiP window's size per device, so the same 320x568-point card is a
 * different share of an iPhone SE, a Pro Max and a tablet. These are floors
 * used only for the edge a frame did not show (Android has no footer; a
 * status line can be blank), and they grow AWAY from the edge that was found.
 */
const HUD_MIN_W = 0.34;
const HUD_MIN_H = 0.28;
const HUD_PAD = 0.04;

interface HudMarker extends NumberBox {
  edge: "top" | "bottom" | "inner";
}

/**
 * Drop everything inside our own HUD card (2026-09-16 owner report). The
 * floating HUD is part of the screen the broadcast captures, so Vision reads
 * it like any other text, and its "CP 319" line sits in the same top band as
 * GO's CP. Read once, the wrong number was shown on the card, read back off
 * the card on the next frame, and stuck: a CP66 Lechonk stayed "CP 319". The
 * card can be dragged to any corner, so it is located by what it says (lines
 * GO never prints) rather than by a fixed position, and every block inside
 * that box goes, CP, HP and headline alike.
 *
 * Geometry (2026-09-17, "all device displays"): the status line is the card's
 * top row and the footer its bottom row, so a frame that shows both fixes the
 * card's height exactly, whatever share of the screen the card is on this
 * device. With one edge missing the box grows the floor height away from the
 * edge it has. Horizontally the markers are left-aligned in the card, so the
 * box grows rightwards from the leftmost marker.
 */
export function dropHudEcho(blocks: OcrTextBlock[]): OcrTextBlock[] {
  const markers: HudMarker[] = [];
  for (const b of blocks) {
    const t = (b.text || "").trim();
    let edge: HudMarker["edge"];
    // Status first: "Already up to date in Nexus Dex" is the card's top row,
    // not its "NEXUS DEX" footer.
    let m = HUD_STATUS_RE.exec(t);
    if (m) edge = "top";
    else if ((m = HUD_FOOTER_RE.exec(t))) edge = "bottom";
    else if ((m = HUD_INNER_RE.exec(t))) edge = "inner";
    else continue;
    if (m[0].length >= t.length - 2) {
      markers.push({ ...b, edge });
      continue;
    }
    // GO's own text on the same row merges with the marker into ONE block
    // (2026-09-17 device frames: the card parked bottom-right put its
    // "NEXUS DEX" footer level with the appraisal sentence, and Vision read
    // "This Fidough was caught ( NExUS DEx" as one line from x=0.07). The
    // whole block is not the card: only the marker's share of it is, taken
    // by character position. Using the whole line grew the box over the
    // appraisal card and took its Attack / Defense / HP labels with it, so
    // the bars were never sampled and the HUD kept asking for an appraisal
    // that was already on screen.
    const start = m.index / t.length;
    const end = (m.index + m[0].length) / t.length;
    markers.push({ x: b.x + b.w * start, y: b.y, w: b.w * (end - start), h: b.h, edge });
  }
  if (markers.length === 0) return blocks;
  let x0 = Math.min(...markers.map((b) => b.x));
  let x1 = Math.max(...markers.map((b) => b.x + b.w));
  let y0 = Math.min(...markers.map((b) => b.y));
  let y1 = Math.max(...markers.map((b) => b.y + b.h));
  const hasTop = markers.some((b) => b.edge === "top");
  const hasBottom = markers.some((b) => b.edge === "bottom");
  if (!hasBottom) y1 = Math.max(y1, y0 + HUD_MIN_H);
  if (!hasTop) y0 = Math.min(y0, y1 - HUD_MIN_H);
  x1 = Math.max(x1, x0 + HUD_MIN_W);
  x0 -= HUD_PAD;
  x1 += HUD_PAD;
  y0 -= HUD_PAD;
  y1 += HUD_PAD;
  return blocks.filter((b) => {
    const bx = b.x + b.w / 2;
    const by = b.y + b.h / 2;
    return !(bx >= x0 && bx <= x1 && by >= y0 && by <= y1);
  });
}

export function dropStatusBarChrome(blocks: OcrTextBlock[]): OcrTextBlock[] {
  return blocks.filter((b) => {
    const cy = b.y + b.h / 2;
    if (cy >= STATUS_BAR_MAX_Y) return true;
    const t = (b.text || "").trim();
    if (t.length === 0) return false;
    return !STATUS_CHROME_RE.test(t);
  });
}

/** The CP banner lives in the top band of the page. */
const CP_BAND_MAX_Y = 0.18;

/** Two blocks share a visual row when their y-centres are within this. */
const ROW_TOLERANCE = 0.018;
/** Name-row candidates within this much of the nearest one compete on centring. */
const NAME_ROW_TOLERANCE = 0.02;

/**
 * Appraisal IV bar geometry, anchored PER LABEL (2026-08-20 device
 * calibration, four real appraisal screenshots at 1179x2556).
 *
 * Each stat label ("Attack" / "Defense" / "HP") sits directly above ITS bar:
 * the bar's left edge aligns with the label's left edge, the bar top sits
 * ~0.011 of frame height below the label bottom, the bar is ~0.009 tall, and
 * every bar is the same fixed length — 0.346 of frame WIDTH.
 *
 * The earlier approach (crop the whole card, let the shared band-finder locate
 * three bars) failed on device for two structural reasons this page cannot
 * avoid: the appraisal card is slightly translucent, so the red page wash
 * tints its "white" toward the grey band the shared empty-pixel class
 * accepts (merging all bands into one blob), and the red page BELOW the card
 * is a solid full-width run of fill-class pixels that forms a phantom band
 * thicker than any real bar (read as a 15). Label-anchored strips never touch
 * either: the strip cannot reach the card edge, and the strict empty band
 * below excludes the tinted card white.
 */
/** Bar length as a fraction of frame WIDTH — identical for all three bars. */
const IV_BAR_LEN_FRAC = 0.346;
/**
 * The same bar on a 4:3 iPad (the owner's Palkia appraisal, 2026-10-23,
 * 1500x2000): the track runs x 0.088-0.349, so 0.261 of the width and one IV
 * is 0.0174 of it. The label-anchored strip (0.37 wide) still covers it.
 */
const IV_BAR_LEN_FRAC_TABLET = 0.261;
/** The known bar length for a frame of this shape (lib/frameGeometry). */
export function ivBarLengthFrac(width: number, height: number): number {
  return classifyFrameAspect(width, height) === "tablet" ? IV_BAR_LEN_FRAC_TABLET : IV_BAR_LEN_FRAC;
}
/** Strip crop: from just under the label to just past the bar. */
const IV_STRIP_TOP_GAP = 0.002;
const IV_STRIP_H = 0.024;
const IV_STRIP_LEFT_PAD = 0.006;
const IV_STRIP_W = 0.37;
/** A strip must show at least this much of the expected bar to be a read. */
const IV_BAR_MIN_COVERAGE = 0.5;

// ── Species name resolution ──────────────────────────────────────────────────

let nameToIdCache: Map<string, number> | null = null;

/**
 * Normalise a printed name for species matching: uppercase alphanumerics,
 * accents folded first. GO prints "Flabébé" while the species table (and a
 * flattening OCR) say "Flabebe"; stripping the accented letters outright
 * left "FLABB" against "FLABEBE", so the Galaxy's HUD flip-flopped between
 * the name and "Appraise it so I can identify it" (owner frames, 2026-09-17).
 */

function nameToId(): Map<string, number> {
  if (nameToIdCache) return nameToIdCache;
  const map = new Map<string, number>();
  for (const [idStr, name] of Object.entries(SPECIES_NAMES)) {
    map.set(normalizeSpeciesToken(name), Number(idStr));
  }
  nameToIdCache = map;
  return map;
}

/** Match a printed name to a national dex id, or null (a nickname). */
export function speciesIdFromDisplayName(name: string | null): number | null {
  if (!name) return null;
  const id = nameToId().get(normalizeSpeciesToken(name));
  if (id != null) return id;
  // "Mega Kyogre" / "Primal Kyogre" (2026-09-09): the Mega Level and Mega
  // Energy pages print the prefixed name, and Primal Reversion prints
  // "Primal". A WORD prefix only, so Meganium stays Meganium.
  const prefixed = /^(?:mega|primal)\s+(.+)$/i.exec(name.trim());
  if (prefixed) return nameToId().get(normalizeSpeciesToken(prefixed[1])) ?? null;
  return null;
}

/**
 * "Nidoran" with nothing, or a one or two character leftover, where the ♀ / ♂
 * was. GO prints both species as "Nidoran" plus a gender glyph, and ML Kit on
 * the Galaxy has no such glyph: it drops it or reads a stray letter, so the
 * name matched neither species and the HUD asked for an appraisal that was
 * already open (2026-10-06 owner frames, a CP 253 Nidoran♀ read as 2/12/7).
 * A name that kept its glyph is an exact match and never gets here.
 */
export function isBareNidoran(name: string | null): boolean {
  if (!name) return false;
  const token = normalizeSpeciesToken(name);
  return /^NIDORAN[A-Z0-9]{0,2}$/.test(token) && !nameToId().has(token);
}

const NIDORAN_F = 29;
const NIDORAN_M = 32;

/**
 * Which Nidoran this is, from what else the frame shows. The gender glyph
 * when it was read; otherwise the stats: the two have different base stats,
 * so with the appraisal bars read, only one of them can print this CP and HP
 * together at any level. Null when neither signal settles it (no appraisal
 * yet), and the HUD's "appraise it" line is then the honest ask.
 */
export function nidoranIdFromStats(read: {
  gender: "male" | "female" | null;
  cp: number | null;
  hp: number | null;
  iv: { atk: number; def: number; hp: number } | null;
}): number | null {
  if (read.gender === "female") return NIDORAN_F;
  if (read.gender === "male") return NIDORAN_M;
  const { cp, hp, iv } = read;
  if (cp == null || hp == null || !iv) return null;
  const fits = [NIDORAN_F, NIDORAN_M].filter((id) => {
    const [atk, def, sta] = SPECIES_BASE_STATS[id];
    const base = { atk, def, sta };
    for (let level = 1; level <= 51; level += 0.5) {
      if (
        computeHp(base, iv.hp, level) === hp &&
        computeCp(base, { atk: iv.atk, def: iv.def, sta: iv.hp }, level) === cp
      ) {
        return true;
      }
    }
    return false;
  });
  return fits.length === 1 ? fits[0] : null;
}

/**
 * Species match for IV-styled nicknames: "Rayquaza¹⁵¹⁴¹⁵", "Mewtwo⑮⑭⑮",
 * "98Machamp" (which is also what Vision reads the superscript forms AS —
 * OCR flattens ⁹⁸ to plain "98"). These are the names our own Naming studio
 * generates and the ones Poke Genie converts carry, and they used to jam
 * species detection ("species_unresolved" on every IV-named Pokémon).
 *
 * Two steps, sharing the generator's own glyph table (ivNicknames) so
 * whatever alphabet the naming side learns, this reader learns with it:
 *   1. decode styled glyphs to ASCII, then normalise as usual;
 *   2. exact match first, then treat DIGIT RUNS AT THE ENDS as stat
 *      decorations: strip progressively from each end and keep the LONGEST
 *      remaining species match, so "PORYGON298" resolves to Porygon2, never
 *      Porygon, and "MEWTWO98" to Mewtwo.
 */
export function speciesIdFromStyledName(name: string | null): number | null {
  if (!name) return null;
  const token = normalizeSpeciesToken(decodeStyledGlyphs(name));
  if (!token) return null;
  const map = nameToId();
  const exact = map.get(token);
  if (exact != null) return exact;
  const lead = /^\d+/.exec(token)?.[0].length ?? 0;
  const trail = /\d+$/.exec(token)?.[0].length ?? 0;
  let best: { id: number; len: number } | null = null;
  for (let l = 0; l <= lead; l++) {
    for (let t = 0; t <= trail; t++) {
      if (l === 0 && t === 0) continue;
      const candidate = token.slice(l, token.length - t);
      // Three-letter floor: "MEW" must come from "MEW…", not digit debris.
      if (candidate.length < 3) continue;
      const id = map.get(candidate);
      if (id != null && (best == null || candidate.length > best.len)) {
        best = { id, len: candidate.length };
      }
    }
  }
  return best?.id ?? null;
}

// ── Pure: page detector ──────────────────────────────────────────────────────

const HP_PATTERN = /(\d{1,4})\s*\/\s*(\d{1,4})\s*HP/i;
const CP_PATTERN = /\bCP\s*([\d,]{2,7})\b/i;
const WEATHER_BONUS = /WEATHER\s*BONUS/i;
const LUCKY_BANNER = /LUCKY\s+POK/i;
/**
 * The Max band GO draws between the weight/height row and the resource grid on
 * an individual that HAS the ability. Anchored to the START of a SHORT block,
 * so it reads the band's own label ("Dynamax") and the Max-moves panel that
 * only ever accompanies it ("Dynamax Level 3"), while a merged paragraph or a
 * long line of chrome that merely mentions the word cannot match.
 *
 * The reader additionally refuses any block ABOVE the HP bar, which is the one
 * place a false positive could otherwise come from: a trainer is free to
 * nickname a Pokémon "Dynamax", and the nickname is printed up there.
 */
/**
 * The label can be CUT SHORT by the HUD card (2026-09-29 owner frames: the
 * card parked bottom-right covers the right half of the band once the page
 * is scrolled to it, and Vision reads "Dyna" / "Dynama" / "Giganta"), so a
 * short block that is a prefix of the word, at least four letters long, is
 * the band too. Nothing else GO prints below the HP bar starts that way.
 */
const GIGANTAMAX_BAND = /^(gigantamax\b|giga(n(t(a(m(a(x)?)?)?)?)?)?$)/i;
const DYNAMAX_BAND = /^(dynamax\b|dyna(m(a(x)?)?)?$)/i;
/** Longest block the band can be. "Dynamax Level 10" is 16. */
const MAX_BAND_MAX_LEN = 24;

/**
 * Which Max band the page prints, or null for a Pokémon that has none.
 * Gigantamax is asked FIRST rather than by block order: the two are drawn in
 * the same slot and an individual carries exactly one of them.
 */
function readMaxBand(
  blocks: OcrTextBlock[],
  hpBlock: OcrTextBlock | null,
): "dynamax" | "gmax" | null {
  const floor = hpBlock ? hpBlock.y : 0;
  const candidates: string[] = [];
  for (const b of blocks) {
    if (b.y <= floor) continue;
    const t = decodeStyledGlyphs(b.text || "").trim();
    if (t.length === 0 || t.length > MAX_BAND_MAX_LEN) continue;
    candidates.push(t);
  }
  if (candidates.some((t) => GIGANTAMAX_BAND.test(t))) return "gmax";
  if (candidates.some((t) => DYNAMAX_BAND.test(t))) return "dynamax";
  return null;
}
/**
 * The Max Moves panel (2026-09-04, from the owner's Zacian and Zamazenta
 * screenshots). Two things on the page say it:
 *
 *   - the pink "Max Moves" ribbon, drawn in the Max band's slot between the
 *     type row and the resource grid, and again as the panel's own header
 *     further down. Either proves the individual HAS Max Moves.
 *   - the three tiles under the header: a state label on each ("MAX!",
 *     "LOCKED", "LV. 2") with the move's name beneath ("Behemoth Bash",
 *     "Guard", "Spirit"). Tiles sit in three columns across the page.
 *
 * Same nickname guard as the Max band: a block above the HP bar is never
 * the ribbon. The tiles need no guard, since a level label is not a word a
 * trainer types.
 */
const MAX_MOVES_RIBBON = /^max\s*moves$/i;
/** Column boundaries for the three tiles (x is a 0..1 fraction of the page). */
const MAX_TILE_COLUMN_SPLITS: readonly [number, number] = [0.4, 0.62];
/** Names sit this far below their state label at most (two-line names included). */
const MAX_TILE_NAME_REACH = 0.1;

export function readMaxMoves(
  blocks: OcrTextBlock[],
  hpBlock: OcrTextBlock | null,
): MyPokemonMaxMovesRead | null {
  const floor = hpBlock ? hpBlock.y : 0;
  const hasRibbon = blocks.some((b) => {
    if (b.y <= floor) return false;
    const t = decodeStyledGlyphs(b.text || "").trim();
    return t.length <= 12 && MAX_MOVES_RIBBON.test(t);
  });

  // State labels, grouped into visual rows; the tile row is the one with
  // the most labels (GO draws three, OCR usually catches at least two).
  const labels = blocks
    .map((b) => ({ block: b, level: parseMaxMoveTileLabel(b.text) }))
    .filter((x): x is { block: OcrTextBlock; level: 0 | 1 | 2 | 3 } => x.level != null && x.block.y > floor);
  const rows: Array<Array<{ block: OcrTextBlock; level: 0 | 1 | 2 | 3 }>> = [];
  for (const l of [...labels].sort((a, b) => a.block.y - b.block.y)) {
    const cy = l.block.y + l.block.h / 2;
    const row = rows.find((r) => Math.abs(r[0].block.y + r[0].block.h / 2 - cy) < ROW_TOLERANCE * 1.5);
    if (row) row.push(l);
    else rows.push([l]);
  }
  const tileRow = rows.sort((a, b) => b.length - a.length)[0];
  if (!hasRibbon && (!tileRow || tileRow.length < 2)) return null;

  const read: MyPokemonMaxMovesRead = { hasMaxMoves: hasRibbon };
  if (!tileRow || tileRow.length < 2) return read;

  const columnOf = (b: OcrTextBlock): 0 | 1 | 2 => {
    const cx = b.x + b.w / 2;
    return cx < MAX_TILE_COLUMN_SPLITS[0] ? 0 : cx < MAX_TILE_COLUMN_SPLITS[1] ? 1 : 2;
  };
  const rowTop = Math.min(...tileRow.map((l) => l.block.y));
  const rowBottom = Math.max(...tileRow.map((l) => l.block.y + l.block.h));
  // The names beneath: every lettered block within reach of the row, by
  // column, top to bottom so a two-line name ("Behemoth" / "Bash") joins.
  const nameParts: string[][] = [[], [], []];
  const below = blocks
    .filter((b) => b.y >= rowBottom - ROW_TOLERANCE && b.y <= rowTop + MAX_TILE_NAME_REACH)
    .filter((b) => !tileRow.some((l) => l.block === b))
    .filter((b) => /[A-Za-z]{3,}/.test(b.text || ""))
    .sort((a, b) => a.y - b.y);
  for (const b of below) {
    const t = decodeStyledGlyphs(b.text || "").trim();
    if (t.length === 0 || t.length > 24) continue;
    if (parseMaxMoveTileLabel(t) != null) continue;
    nameParts[columnOf(b)].push(t);
  }
  const levelByColumn: Array<0 | 1 | 2 | 3 | null> = [null, null, null];
  for (const l of tileRow) {
    const col = columnOf(l.block);
    if (levelByColumn[col] == null) levelByColumn[col] = l.level;
  }
  // Attack tile: the level, and the move name when it is one we know.
  const attackLevel = levelByColumn[0];
  if (attackLevel != null && attackLevel > 0) {
    const match = matchMaxMoveName(nameParts[0].join(" "));
    read.attack = {
      name: match && match.kind !== "guard" && match.kind !== "spirit" ? match.name : null,
      level: attackLevel as 1 | 2 | 3,
    };
    read.hasMaxMoves = true;
  }
  if (levelByColumn[1] != null) {
    read.guard = levelByColumn[1];
    read.hasMaxMoves = true;
  }
  if (levelByColumn[2] != null) {
    read.spirit = levelByColumn[2];
    read.hasMaxMoves = true;
  }
  return read;
}

const MOVES_TAB = /GYMS\s*&\s*RAIDS|TRAINER\s*BATTLES/i;
const POWER_UP = /^\s*POWER\s*UP\s*$/i;
/**
 * Two shapes for the appraisal sheet sentence. The FULL one captures date +
 * location; the SPECIES-ONLY prefix exists because our own HUD floats over
 * the sheet's middle and routinely clips everything after "was caught" (the
 * 2026-08-20 device log shows every nicknamed Pokémon stuck unresolved for
 * exactly this reason) — and the species name sits safely BEFORE the clip.
 */
// The name class takes accented Latin letters (\u00C0-\u024F): "This Flabébé
// was caught on…" matched nothing without them, and the sheet is the one
// resolver a nicknamed or misread name cannot break.
const APPRAISAL_SENTENCE =
  /This\s+([A-Za-z0-9\u00C0-\u024F'.:♀♂ -]+?)\s+was\s+caught\s+on\s+([\d/.-]+)\s+around\s+(.+?)\.?\s*$/i;
const APPRAISAL_SENTENCE_PREFIX = /This\s+([A-Za-z0-9\u00C0-\u024F'.:♀♂ -]{2,}?)\s+was\b/i;

/** Appraisal overlay stat labels are Title Case, on the card's left half. */
function isAppraisalStatLabel(b: OcrTextBlock, word: string): boolean {
  const t = (b.text || "").trim();
  return t === word && b.x < 0.55 && b.y > 0.4;
}

/**
 * Decide whether OCR blocks belong to an individual caught-Pokémon page.
 *
 * The strongest stable anchors, across both views and all scroll positions:
 * the "cur / max HP" readout, the WEIGHT+HEIGHT stat pair, the STARDUST label,
 * the POWER UP button, and the moves tab row. The appraisal overlay occludes
 * most of those, so it qualifies on its own signature: the Attack/Defense/HP
 * stat labels (Title Case, unlike this page's UPPERCASE labels) or the
 * "This X was caught on …" sheet, anchored by the HP readout or CP banner
 * still visible at the top.
 *
 * The Pokédex SPECIES page never shows an HP readout, POWER UP, STARDUST or a
 * moveset — and the live loop only probes this detector AFTER that page's own
 * probe has said no — so the two cannot claim the same frame.
 */
/**
 * The storage box: a grid of caught Pokémon with a "CP123" label under each.
 * A single Pokémon's page has exactly one CP, in its header band; four or
 * more CP labels spread down the frame is the box (2026-09-03).
 */
export function looksLikeStorageBox(blocks: OcrTextBlock[]): boolean {
  const cpLabels = blocks.filter((b) => /^CP\s?\d{1,4}$/i.test((b.text || "").trim()));
  if (cpLabels.length < 4) return false;
  const ys = cpLabels.map((b) => b.y);
  return Math.max(...ys) - Math.min(...ys) > 0.15;
}

export function looksLikeMyPokemonPage(blocks: OcrTextBlock[]): boolean {
  const texts = blocks.map((b) => b.text || "");
  const has = (re: RegExp) => texts.some((t) => re.test(t));

  const hasHp = has(HP_PATTERN);
  const hasCp = blocks.some(
    (b) => CP_PATTERN.test(b.text || "") && b.y < CP_BAND_MAX_Y,
  );
  const hasWeightHeight = has(/\bWEIGHT\b/i) && has(/\bHEIGHT\b/i);
  const hasStardust = has(/\bSTARDUST\b/i);
  const hasPowerUp = texts.some((t) => POWER_UP.test(t));
  const hasMovesTab = has(MOVES_TAB);
  // A DEEP-SCROLLED page (tall species like Zamazenta) shows none of the top
  // anchors — its signature is the buddy button and the provenance card.
  const hasLowerPage =
    has(/SWAP\s*BUDDIES/i) ||
    has(/CAUGHT\s+IN\s+THE\s+WILD|OBTAINED\s+IN\s+A\s+TRADE|REMOTELY\s+CAUGHT/i);

  const detailAnchors = [
    hasHp,
    hasWeightHeight,
    hasStardust,
    hasPowerUp,
    hasMovesTab,
    hasLowerPage,
  ].filter(Boolean).length;
  if (detailAnchors >= 2) return true;
  if (hasCp && detailAnchors >= 1) return true;

  // Appraisal overlay: the stat labels + something tying it to this page.
  const hasStatLabels =
    blocks.some((b) => isAppraisalStatLabel(b, "Attack")) &&
    blocks.some((b) => isAppraisalStatLabel(b, "Defense"));
  const hasSheet = texts.some((t) => APPRAISAL_SENTENCE.test(t));
  if ((hasStatLabels || hasSheet) && (hasHp || hasCp)) return true;
  return false;
}

// ── Pure: field parsers ──────────────────────────────────────────────────────

function parseNumber(s: string): number | null {
  const cleaned = (s || "").replace(/[,\s]/g, "");
  if (!/^\d+$/.test(cleaned)) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

/** Read the CP banner. Only a block that carries the "CP" prefix counts — a
 * bare number in the top band could be a partially occluded CP (the sprite
 * can cover the leading digits), and committing a truncated CP is worse than
 * committing none: the merge layer fills it from a later cleaner frame. */
export interface EncounterBannerRead {
  speciesId: number;
  speciesName: string;
  cp: number;
}

// The encounter banner sits over the Pokémon, a third of the way down: below
// a caught-Pokémon page's CP band and above the page's own name row.
const ENCOUNTER_BAND_MIN_Y = 0.16;
const ENCOUNTER_BAND_MAX_Y = 0.5;
const ENCOUNTER_ROW_TOLERANCE = 0.025;

/**
 * The wild-encounter banner (2026-09-22): "Sprigatito / CP353" on one line
 * over the Pokémon, a third of the way down the frame. The species may share
 * the CP's block ("Sprigatito / CP353") or sit in its own block on the same
 * row to the left. Exactly ONE CP in the band, and a species that resolves,
 * or nothing: a raid lobby's boss CP has no species on its row and a
 * storage box has many CPs.
 */
export function readEncounterBanner(blocks: OcrTextBlock[]): EncounterBannerRead | null {
  const inBand = blocks.filter((b) => {
    const cy = b.y + b.h / 2;
    return cy >= ENCOUNTER_BAND_MIN_Y && cy <= ENCOUNTER_BAND_MAX_Y && CP_PATTERN.test(b.text || "");
  });
  if (inBand.length !== 1) return null;
  const cpBlock = inBand[0];
  const m = (cpBlock.text || "").match(CP_PATTERN);
  const cp = m ? parseNumber(m[1]) : null;
  if (cp == null || cp < 10 || cp > 9999) return null;
  const resolve = (raw: string): { id: number; name: string } | null => {
    const cleaned = raw.replace(/[|/\\_:;.,]+$/g, "").trim();
    if (!/[A-Za-z]{2,}/.test(cleaned)) return null;
    const id = speciesIdFromDisplayName(cleaned);
    return id != null ? { id, name: cleaned } : null;
  };
  // Same block: whatever precedes "CP".
  const before = (cpBlock.text || "").slice(0, m!.index).trim();
  let hit = before ? resolve(before) : null;
  if (!hit) {
    // Own block on the same row, to the left.
    const cy = cpBlock.y + cpBlock.h / 2;
    const row = blocks
      .filter((b) => b !== cpBlock && Math.abs(b.y + b.h / 2 - cy) <= ENCOUNTER_ROW_TOLERANCE && b.x < cpBlock.x)
      .sort((a, b) => b.x - a.x);
    for (const b of row) {
      hit = resolve((b.text || "").trim());
      if (hit) break;
    }
  }
  if (!hit) return null;
  return { speciesId: hit.id, speciesName: hit.name, cp };
}

export function readCp(blocks: OcrTextBlock[]): number | null {
  // GO centres the CP banner over the sprite. With more than one candidate
  // in the band (our HUD's own CP line, a badge), the centred one is GO's;
  // Vision's block order is not top-to-bottom, so "first match" was luck.
  let best: { n: number; off: number } | null = null;
  for (const b of blocks) {
    if (b.y >= CP_BAND_MAX_Y) continue;
    const m = (b.text || "").match(CP_PATTERN);
    if (!m) continue;
    const n = parseNumber(m[1]);
    if (n == null || n < 10 || n > 9999) continue;
    const off = Math.abs(b.x + b.w / 2 - 0.5);
    if (!best || off < best.off) best = { n, off };
  }
  return best?.n ?? null;
}

export function readHp(
  blocks: OcrTextBlock[],
): { current: number; max: number; block: OcrTextBlock } | null {
  for (const b of blocks) {
    const m = (b.text || "").match(HP_PATTERN);
    if (!m) continue;
    const current = Number(m[1]);
    const max = Number(m[2]);
    if (
      Number.isFinite(current) &&
      Number.isFinite(max) &&
      max >= 10 &&
      max <= 999 &&
      current <= max
    ) {
      return { current, max, block: b };
    }
  }
  return null;
}

/**
 * The display name sits directly above the HP readout (with the HP bar
 * between them). Take the nearest text block above the HP block whose text
 * is a plausible name — not the LUCKY banner, not the CP banner, not a
 * date badge.
 */
export function readDisplayName(
  blocks: OcrTextBlock[],
  hpBlock: OcrTextBlock,
): string | null {
  const hpCy = hpBlock.y + hpBlock.h / 2;
  const candidates: { block: OcrTextBlock; dy: number; dx: number }[] = [];
  for (const b of blocks) {
    if (b === hpBlock) continue;
    const t = (b.text || "").trim();
    // Gates run on the DECODED text (2026-08-31): a glyph-styled nickname
    // ("Zubat²²²", or a circled-digit triple with a short species prefix)
    // can carry few plain letters and more code points than its visual
    // length, and the raw-text gates were silently dropping exactly the
    // names the naming feature generates. The RAW text is still what gets
    // returned — decoding here is only for the plausibility checks.
    const decoded = decodeStyledGlyphs(t);
    if (decoded.length < 2 || decoded.length > 24) continue;
    if (!/[A-Za-z]{2,}/.test(decoded)) continue;
    if (LUCKY_BANNER.test(decoded)) continue;
    if (CP_PATTERN.test(decoded)) continue;
    if (/\d{1,2}\/\d{1,2}/.test(decoded)) continue; // date badge
    const cy = b.y + b.h / 2;
    const dy = hpCy - cy;
    if (dy <= 0.015 || dy > 0.14) continue; // must be above, and near
    candidates.push({ block: b, dy, dx: Math.abs(b.x + b.w / 2 - 0.5) });
  }
  if (candidates.length === 0) return null;
  // Nearest row above the HP readout, then the block nearest the CENTRE of
  // that row: GO centres the name, and the gender glyph at the row's right
  // edge is read as a short word now and then ("Ot" for ♀ on the Galaxy,
  // 2026-09-17), which by vertical distance alone tied with the name and
  // won on jitter, so the HUD flipped between "Flabébé" and "Ot".
  const nearest = Math.min(...candidates.map((c) => c.dy));
  const row = candidates.filter((c) => c.dy <= nearest + NAME_ROW_TOLERANCE);
  row.sort((a, b) => a.dx - b.dx || a.dy - b.dy);
  const best = row[0];
  // Strip the edit-pencil artifacts OCR sometimes appends.
  return best.block.text.trim().replace(/[|/\\_:;.,]+$/g, "").trim();
}

export function readWeightHeight(blocks: OcrTextBlock[]): {
  weightKg: number | null;
  heightM: number | null;
  sizeClass: MyPokemonSizeClass;
} {
  let weightKg: number | null = null;
  let heightM: number | null = null;
  let weightY: number | null = null;
  let heightY: number | null = null;
  for (const b of blocks) {
    const t = (b.text || "").trim();
    let m = t.match(/^([\d,]+(?:\.\d+)?)\s*kg$/i);
    if (m && weightKg == null) {
      const n = Number(m[1].replace(/,/g, ""));
      if (Number.isFinite(n) && n > 0 && n < 1000) {
        weightKg = n;
        weightY = b.y + b.h / 2;
      }
      continue;
    }
    m = t.match(/^([\d,]+(?:\.\d+)?)\s*m$/i);
    if (m && heightM == null) {
      const n = Number(m[1].replace(/,/g, ""));
      if (Number.isFinite(n) && n > 0 && n < 100) {
        heightM = n;
        heightY = b.y + b.h / 2;
      }
    }
  }
  // GO prints a size-class badge ("XXS"/"XS"/"XL"/"XXL") in the weight/height
  // row for extreme sizes. Only a block VERTICALLY ON that row counts — the
  // same letters can appear as user tag pills near the name, and those must
  // never become a size claim.
  let sizeClass: MyPokemonSizeClass = null;
  const rowY = weightY ?? heightY;
  if (rowY != null) {
    for (const b of blocks) {
      const t = (b.text || "").trim().toUpperCase();
      if (t !== "XXS" && t !== "XS" && t !== "XL" && t !== "XXL") continue;
      if (Math.abs(b.y + b.h / 2 - rowY) > 0.05) continue;
      sizeClass = t.toLowerCase() as MyPokemonSizeClass;
      break;
    }
  }
  return { weightKg, heightM, sizeClass };
}

/**
 * Types are printed in the centre of the weight/height row ("NORMAL",
 * "BUG / WATER"). Validate every token against the real type list so a stray
 * uppercase word can never become a type.
 */
export function readTypes(blocks: OcrTextBlock[]): PokeType[] {
  const typeSet = new Set<string>(ALL_TYPES.map((t) => t.key));
  // Anchor on the WEIGHT / HEIGHT labels' row when present, else scan all.
  const weight = blocks.find((b) => /^WEIGHT$/i.test((b.text || "").trim()));
  const out: PokeType[] = [];
  for (const b of blocks) {
    if (weight) {
      const dy = Math.abs(b.y + b.h / 2 - (weight.y + weight.h / 2));
      if (dy > 0.06) continue;
    }
    const tokens = (b.text || "").toUpperCase().split(/[\s/]+/);
    for (const tok of tokens) {
      const lower = tok.toLowerCase();
      if (typeSet.has(lower) && !out.includes(lower as PokeType)) {
        out.push(lower as PokeType);
      }
    }
    if (out.length >= 2) break;
  }
  return out;
}

/** The number for a resource label ("STARDUST") is printed just above/beside
 * it. Find the numeric block nearest the label, biased upward. */
function numberNearLabel(
  blocks: OcrTextBlock[],
  label: OcrTextBlock,
): number | null {
  const lcx = label.x + label.w / 2;
  const lcy = label.y + label.h / 2;
  let best: { n: number; d: number } | null = null;
  for (const b of blocks) {
    if (b === label) continue;
    const n = parseNumber((b.text || "").trim());
    if (n == null) continue;
    const cx = b.x + b.w / 2;
    const cy = b.y + b.h / 2;
    const dy = lcy - cy; // positive = number above label
    if (dy < -0.01 || dy > 0.06) continue;
    const d = Math.abs(cx - lcx) + dy * 0.5;
    if (Math.abs(cx - lcx) > 0.25) continue;
    if (!best || d < best.d) best = { n, d };
  }
  return best ? best.n : null;
}

/** One "<SPECIES> MEGA ENERGY [X|Y]" figure off the page. */
export interface MegaEnergyReading {
  speciesName: string;
  /** "" for a species with one Mega, "X" or "Y" for a pair. */
  formLabel: string;
  energy: number;
}

/**
 * The X/Y letter of a "MEGA ENERGY X" label. GO wraps the letter onto its
 * own line under the label ("MEWTWO MEGA ENERGY" / "X"), so OCR may hand it
 * back as a separate one-letter block just below the label's centre.
 */
function megaFormLetterBelow(blocks: OcrTextBlock[], label: OcrTextBlock): string {
  const lcx = label.x + label.w / 2;
  const lcy = label.y + label.h / 2;
  let best: { letter: string; d: number } | null = null;
  for (const b of blocks) {
    if (b === label) continue;
    const t = (b.text || "").trim().toUpperCase();
    if (t !== "X" && t !== "Y") continue;
    const cx = b.x + b.w / 2;
    const cy = b.y + b.h / 2;
    const dy = cy - lcy; // positive = letter below label
    if (dy < 0 || dy > 0.05) continue;
    if (Math.abs(cx - lcx) > 0.2) continue;
    const d = Math.abs(cx - lcx) + dy;
    if (!best || d < best.d) best = { letter: t, d };
  }
  return best?.letter ?? "";
}

/**
 * "<SPECIES> MEGA ENERGY", "<SPECIES> MEGA ENERGY X", or the Mega Level
 * page's "MEGA <SPECIES> X MEGA ENERGY". The leading MEGA needs a space
 * after it so "MEGANIUM MEGA ENERGY" keeps its name. Primal Reversion
 * (2026-09-09, device screenshots) prints "KYOGRE PRIMAL ENERGY": same
 * shape, the word PRIMAL where MEGA would be.
 */
const MEGA_ENERGY_LABEL =
  /^(?:(?:MEGA|PRIMAL)\s+)?([A-Z. -]+?)(?:\s+([XY]))?\s+(?:MEGA|PRIMAL)\s*ENERGY(?:\s+([XY]))?$/;

/** The number printed on the same row to the right of a label. */
function numberBesideLabel(blocks: OcrTextBlock[], label: OcrTextBlock): number | null {
  const lcy = label.y + label.h / 2;
  let best: { n: number; d: number } | null = null;
  for (const b of blocks) {
    if (b === label || b.x <= label.x + label.w * 0.5) continue;
    const n = parseNumber((b.text || "").trim());
    if (n == null) continue;
    const cy = b.y + b.h / 2;
    if (Math.abs(cy - lcy) > 0.02) continue;
    const d = b.x - (label.x + label.w);
    if (!best || d < best.d) best = { n, d };
  }
  return best ? best.n : null;
}

/* ── The Mega Level circles ──────────────────────────────────────────────── */

export interface MegaFormRead {
  /** "" for a species with one Mega, "X" or "Y" for a pair. */
  formLabel: string;
  /** Circles filled, 0 (never Mega Evolved) to 4 (Super Max). */
  level: 0 | 1 | 2 | 3 | 4;
  /** How far the circle after the last full one is filled (0..1). */
  progressToNext: number;
}

/** The pink of a reached level circle (GO's Mega pink, ~R235 G81 B129). */
function isMegaCirclePink(r: number, g: number, b: number): boolean {
  return r > 180 && g < 140 && b > 80 && r - g > 70;
}
/** The grey of an unreached circle (~R216 G216 B216); the page white is brighter. */
function isMegaCircleGrey(r: number, g: number, b: number): boolean {
  const mx = Math.max(r, g, b);
  const mn = Math.min(r, g, b);
  return mx - mn < 18 && mx > 150 && mx < 225;
}

// The Primal page's button reads PRIMAL REVERSION (the BONUSES heading below
// it is longer, so the anchors keep that out).
const MEGA_EVOLVE_BUTTON = /^(?:MEGA\s*EVOLVE|PRIMAL\s*REVERSION)$/i;
const MEGA_FORM_ROW_LABEL = /^MEGA\s+[A-Z. -]+?\s+([XY])$/i;

/**
 * Read the level circles off a caught-Pokémon page (owner screenshots,
 * 2026-09-03): under each MEGA EVOLVE button sits a row of four small discs,
 * pink for a reached level, grey for the rest, the next one part-filled from
 * the left by the evolutions done toward it. An X/Y species shows two rows
 * with "MEGA <SPECIES> X" / "Y" to the right; a single-Mega species shows
 * one row with no label.
 *
 * Pixel-read from the strip under the first Mega anchor (a MEGA ENERGY
 * label or the MEGA EVOLVE button): every second row is classified into
 * pink / grey runs, a row with four evenly spaced disc-wide runs is a
 * circle row, adjacent rows form a band, and each disc's fill is its pink
 * share of the pink + grey pixels across the band (the white helix glyph
 * counts for neither side). Reads come back top to bottom.
 */
export function readMegaLevelCircles(
  blocks: OcrTextBlock[],
  cropRgba: (roi: OcrRegion) => { data: Uint8ClampedArray | Uint8Array; width: number; height: number } | null,
): MegaFormRead[] {
  const anchors = blocks.filter((b) => {
    const t = (b.text || "").trim().toUpperCase();
    return MEGA_ENERGY_LABEL.test(t) || MEGA_EVOLVE_BUTTON.test(t);
  });
  if (anchors.length === 0) return [];
  const floorY = Math.min(...anchors.map((a) => a.y));
  const roi: OcrRegion = { x: 0.05, y: floorY, w: 0.45, h: Math.min(1 - floorY, 0.55) };
  if (roi.h < 0.05) return [];
  const crop = cropRgba(roi);
  if (!crop || crop.width < 40 || crop.height < 10) return [];
  const { data, width, height } = crop;
  const fullW = width / roi.w; // the frame's width in this crop's pixels
  const discMin = fullW * 0.018;
  const discMax = fullW * 0.05;
  const gapMerge = fullW * 0.012;

  type Disc = { x0: number; x1: number; p: number; g: number };
  const rows: Array<{ y: number; discs: Disc[] }> = [];
  for (let y = 0; y < height; y += 2) {
    const runs: Disc[] = [];
    let cur: Disc | null = null;
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4;
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const pink = isMegaCirclePink(r, g, b);
      const grey = !pink && isMegaCircleGrey(r, g, b);
      if (!pink && !grey) continue;
      if (cur && x - cur.x1 <= gapMerge) {
        cur.x1 = x;
      } else {
        cur = { x0: x, x1: x, p: 0, g: 0 };
        runs.push(cur);
      }
      if (pink) cur.p += 1;
      else cur.g += 1;
    }
    const discs = runs.filter((d) => d.x1 - d.x0 >= discMin && d.x1 - d.x0 <= discMax).slice(0, 4);
    if (discs.length < 4) continue;
    // Evenly spaced, and all four inside the left 45% of the page.
    const centres = discs.map((d) => (d.x0 + d.x1) / 2);
    const gaps = [1, 2, 3].map((k) => centres[k] - centres[k - 1]);
    const gMin = Math.min(...gaps);
    const gMax = Math.max(...gaps);
    if (gMin < fullW * 0.045 || gMax > fullW * 0.1 || gMax / gMin > 1.3) continue;
    rows.push({ y, discs });
  }
  if (rows.length === 0) return [];

  // Circle rows within half a disc of each other form a band (one row of
  // discs): the white glyph through the middle leaves a gap of rows, and
  // only rows near the equator are wide enough to qualify at all. Each
  // disc's fill is read along its WIDEST qualifying row, where the pink
  // share of the chord equals the slice GO fills from the left.
  type Best = { p: number; g: number };
  const bands: Array<{ y0: number; y1: number; rows: number; best: Best[] }> = [];
  for (const row of rows) {
    const last = bands[bands.length - 1];
    if (last && row.y - last.y1 <= discMax * 0.6) {
      last.y1 = row.y;
      last.rows += 1;
      row.discs.forEach((d, i) => {
        if (d.p + d.g > last.best[i].p + last.best[i].g) last.best[i] = { p: d.p, g: d.g };
      });
    } else {
      bands.push({ y0: row.y, y1: row.y, rows: 1, best: row.discs.map((d) => ({ p: d.p, g: d.g })) });
    }
  }
  const reads: Array<{ y: number; level: 0 | 1 | 2 | 3 | 4; progressToNext: number }> = [];
  for (const band of bands) {
    if (band.rows < 2) continue; // a stray row, not a disc
    const fills = band.best.map((s) => (s.p + s.g > 0 ? s.p / (s.p + s.g) : 0));
    let level = 0;
    while (level < 4 && fills[level] >= 0.8) level += 1;
    const nextFill = level < 4 ? fills[level] : 0;
    const progressToNext = nextFill >= 0.04 && nextFill < 0.8 ? Math.round(nextFill * 100) / 100 : 0;
    reads.push({
      y: roi.y + ((band.y0 + band.y1) / 2 / height) * roi.h,
      level: level as 0 | 1 | 2 | 3 | 4,
      progressToNext,
    });
  }
  if (reads.length === 0) return [];

  // The form each row belongs to: its "MEGA <SPECIES> X/Y" label when the
  // page prints one, else by order (a pair reads X then Y, one Mega reads "").
  const labels = blocks.filter((b) => MEGA_FORM_ROW_LABEL.test((b.text || "").trim()));
  return reads.map((r, i) => {
    const label = labels.find((b) => Math.abs(b.y + b.h / 2 - r.y) < 0.02);
    const letter = label
      ? (MEGA_FORM_ROW_LABEL.exec((label.text || "").trim())?.[1] ?? "").toUpperCase()
      : reads.length > 1
        ? i === 0
          ? "X"
          : "Y"
        : "";
    return { formLabel: letter, level: r.level, progressToNext: r.progressToNext };
  });
}

/** "4 DAYS", "3 Days 1 Hour", "20 HOURS", "45 MINUTES" → ms, or null. */
export function parseRestText(text: string): number | null {
  const t = text.trim();
  const re = /(\d+)\s*(DAYS?|HOURS?|HRS?|MINUTES?|MINS?)\b/gi;
  let ms = 0;
  let any = false;
  let m: RegExpExecArray | null;
  while ((m = re.exec(t))) {
    const n = Number(m[1]);
    const unit = m[2].toUpperCase();
    if (unit.startsWith("D")) ms += n * 86_400_000;
    else if (unit.startsWith("H")) ms += n * 3_600_000;
    else ms += n * 60_000;
    any = true;
  }
  // Reject a line with anything else in it ("Rest period 3 days" is fine,
  // "3 Days ago in Sydney" is not): every token must be a number or a unit.
  if (!any) return null;
  const leftover = t.replace(re, "").replace(/REST\s*PERIOD/i, "").trim();
  if (leftover.length > 0) return null;
  return ms;
}

/**
 * The rest period the caught-Pokémon page prints beside the Mega badge
 * ("4 DAYS" between the weight row and the stardust figure), as ms left,
 * or null when the page shows none (no rest running, or not a Mega).
 */
export function readMegaRest(blocks: OcrTextBlock[]): number | null {
  const stardust = blocks.find((b) => /^STARDUST$/i.test((b.text || "").trim()));
  const weight = blocks.find((b) => /^WEIGHT$/i.test((b.text || "").trim()));
  const floor = weight ? weight.y : 0.1;
  const ceil = stardust ? stardust.y : 0.9;
  for (const b of blocks) {
    if (b.y < floor || b.y > ceil) continue;
    const ms = parseRestText(b.text || "");
    if (ms != null && ms > 0) return ms;
  }
  return null;
}

/* ── The Mega Level page ─────────────────────────────────────────────────── */

export interface MegaLevelPageRead {
  /** "MEWTWO", from "Mewtwo's Mega Level". */
  speciesName: string | null;
  /** The form the page is open on, from its energy label: "" / "X" / "Y". */
  formLabel: string;
  /** From the banner: Base 1, High 2, Max 3, Super Max 4. */
  level: 1 | 2 | 3 | 4 | null;
  /** "Mega Evolve Mewtwo 4 more times." / "Have Kyogre undergo Primal Reversion 21 more times." */
  remainingToNext: number | null;
  /** The REST PERIOD figure, as ms left. */
  restMs: number | null;
  /** The form's Mega Energy, printed at the foot of the page. */
  energy: number | null;
  /** "LEVEL UP" cost in energy, when offered. */
  levelUpCost: number | null;
}

// "Mewtwo's Mega Level" / "Kyogre's Primal Level" (2026-09-09 screenshot).
const MEGA_LEVEL_TITLE = /^(.+?)['’`]?S\s+(?:MEGA|PRIMAL)\s+LEVEL$/i;
const MEGA_LEVEL_BANNER = /^(BASE|HIGH|MAX|SUPER\s*MAX)\s+LEVEL$/i;

/** GO's Mega Level page: the title plus the banner or the rest period. */
export function looksLikeMegaLevelPage(blocks: OcrTextBlock[]): boolean {
  const texts = blocks.map((b) => (b.text || "").trim());
  const hasTitle = texts.some((t) => MEGA_LEVEL_TITLE.test(t));
  const hasBanner = texts.some((t) => MEGA_LEVEL_BANNER.test(t));
  const hasRest = texts.some((t) => /REST\s*PERIOD/i.test(t));
  return hasTitle && (hasBanner || hasRest);
}

export function readMegaLevelPage(blocks: OcrTextBlock[]): MegaLevelPageRead {
  let speciesName: string | null = null;
  let level: MegaLevelPageRead["level"] = null;
  let remainingToNext: number | null = null;
  let restMs: number | null = null;
  let energy: number | null = null;
  let formLabel = "";
  let levelUpCost: number | null = null;
  for (const b of blocks) {
    const raw = (b.text || "").trim();
    const t = raw.toUpperCase();
    const title = MEGA_LEVEL_TITLE.exec(raw);
    if (title) {
      speciesName = speciesName ?? title[1].trim().toUpperCase();
      continue;
    }
    const banner = MEGA_LEVEL_BANNER.exec(raw);
    if (banner) {
      const key = banner[1].toUpperCase().replace(/\s+/g, " ");
      level = key === "BASE" ? 1 : key === "HIGH" ? 2 : key === "MAX" ? 3 : 4;
      continue;
    }
    const more = /(\d+)\s+MORE\s+TIMES?/i.exec(raw);
    if (more) {
      remainingToNext = Number(more[1]);
      continue;
    }
    if (/^REST\s*PERIOD$/i.test(raw)) {
      // The figure sits just above the label.
      const lcx = b.x + b.w / 2;
      let best: { ms: number; d: number } | null = null;
      for (const o of blocks) {
        if (o === b) continue;
        const dy = b.y - (o.y + o.h);
        if (dy < -0.01 || dy > 0.06) continue;
        if (Math.abs(o.x + o.w / 2 - lcx) > 0.3) continue;
        const ms = parseRestText(o.text || "");
        if (ms == null) continue;
        if (!best || dy < best.d) best = { ms, d: dy };
      }
      if (best) restMs = best.ms;
      continue;
    }
    if (MEGA_ENERGY_LABEL.test(t)) {
      const m = MEGA_ENERGY_LABEL.exec(t)!;
      formLabel = (m[2] ?? m[3] ?? "").toUpperCase();
      energy = energy ?? numberBesideLabel(blocks, b) ?? numberNearLabel(blocks, b);
      speciesName = speciesName ?? m[1].trim();
      continue;
    }
    if (/^LEVEL\s*UP$/i.test(raw)) {
      levelUpCost = numberBesideLabel(blocks, b);
    }
  }
  return { speciesName, formLabel, level, remainingToNext, restMs, energy, levelUpCost };
}

export function readResources(blocks: OcrTextBlock[]): {
  stardust: number | null;
  candy: number | null;
  candyXl: number | null;
  candyFamilyName: string | null;
  megaEnergy: number | null;
  megaEnergySpeciesName: string | null;
  megaEnergies: MegaEnergyReading[];
} {
  let stardust: number | null = null;
  let candy: number | null = null;
  let candyXl: number | null = null;
  let candyFamilyName: string | null = null;
  let megaEnergy: number | null = null;
  let megaEnergySpeciesName: string | null = null;
  const megaEnergies: MegaEnergyReading[] = [];
  for (const b of blocks) {
    const t = (b.text || "").trim().toUpperCase();
    if (/^STARDUST$/.test(t)) {
      stardust = stardust ?? numberNearLabel(blocks, b);
    } else if (MEGA_ENERGY_LABEL.test(t)) {
      // "METAGROSS MEGA ENERGY" sits beside the candy on a Mega-capable
      // species' page, its count printed above it like the candy's. An X/Y
      // species prints two ("MEWTWO MEGA ENERGY X" and "... Y"), the letter
      // sometimes wrapped onto its own line below. The Mega Level page
      // prints "MEGA MEWTWO X MEGA ENERGY" with the count BESIDE it.
      const m = MEGA_ENERGY_LABEL.exec(t)!;
      const energy = numberNearLabel(blocks, b) ?? numberBesideLabel(blocks, b);
      const name = m[1].trim();
      const letter = m[2] ?? m[3] ?? megaFormLetterBelow(blocks, b);
      if (energy != null && !megaEnergies.some((e) => e.speciesName === name && e.formLabel === letter)) {
        megaEnergies.push({ speciesName: name, formLabel: letter, energy });
      }
      megaEnergy = megaEnergy ?? energy;
      megaEnergySpeciesName = megaEnergySpeciesName ?? name;
    } else if (/^([A-Z. -]+?)\s+CANDY\s*XL$/.test(t)) {
      candyXl = candyXl ?? numberNearLabel(blocks, b);
      candyFamilyName =
        candyFamilyName ?? t.replace(/\s+CANDY\s*XL$/, "").trim();
    } else if (/^([A-Z. -]+?)\s+CANDY$/.test(t)) {
      candy = candy ?? numberNearLabel(blocks, b);
      candyFamilyName = candyFamilyName ?? t.replace(/\s+CANDY$/, "").trim();
    }
  }
  return { stardust, candy, candyXl, candyFamilyName, megaEnergy, megaEnergySpeciesName, megaEnergies };
}

/**
 * User tag pills sit between the HP readout and the weight/height row
 * ("GBL", "Aged", "Max", ".BLUE"). Arbitrary user text — the only shaping is
 * length and the exclusion of the page's own labels in that band.
 */
export function readTags(
  blocks: OcrTextBlock[],
  hpBlock: OcrTextBlock,
): string[] {
  // The pill band sits between the HP readout and the stat row — but the stat
  // row's VALUES ("104.13kg") print ABOVE their labels ("WEIGHT"), so the
  // bottom bound must clear the values, not just the label row.
  const weight = blocks.find((b) => /^WEIGHT$/i.test((b.text || "").trim()));
  const topY = hpBlock.y + hpBlock.h;
  const bottomY = Math.min(weight ? weight.y : 1, topY + 0.09);
  const tags: string[] = [];
  for (const b of blocks) {
    const cy = b.y + b.h / 2;
    if (cy <= topY || cy >= bottomY) continue;
    const t = (b.text || "").trim();
    if (t.length < 2 || t.length > 16) continue;
    if (!/[A-Za-z]/.test(t)) continue;
    if (LUCKY_BANNER.test(t)) continue;
    if (HP_PATTERN.test(t)) continue;
    // Stat-row spillover, never a user tag (2026-08-20 Honedge device pass:
    // "HEAVIEST", "XL" and "EIGHT" — HEIGHT with its H clipped by OCR — all
    // landed as tags when the record-badge page layout pushed the stat row
    // into the tag band). The EIGH fragment catches every WEIGHT/HEIGHT
    // misread; the badge list is PoGo's own weight/height record labels.
    if (/EIGH/i.test(t)) continue; // WEIGHT / HEIGHT / EIGHT / WEIGH…
    if (/^(HP|HEAVIEST|LIGHTEST|TALLEST|SHORTEST|SMALLEST|XL|XS|XXL|XXS)$/i.test(t)) continue;
    // A measurement or a type word is the stat row, not a tag.
    if (/^[\d.,]+\s*(kg|m)$/i.test(t)) continue;
    const typeSet = new Set<string>(ALL_TYPES.map((x) => x.key));
    const tokens = t.toLowerCase().split(/[\s/]+/).filter(Boolean);
    if (tokens.length > 0 && tokens.every((tok) => typeSet.has(tok))) continue;
    if (!tags.includes(t)) tags.push(t);
  }
  return tags;
}

/** Gender glyph, when Vision manages to read it (right of the name). */
export function readGender(blocks: OcrTextBlock[]): "male" | "female" | null {
  for (const b of blocks) {
    const t = (b.text || "").trim();
    if (t === "♀") return "female";
    if (t === "♂") return "male";
  }
  return null;
}

const MOVE_SECTION_END =
  /SWAP\s*BUDDIES|ADVENTURE\s*EFFECTS|MAX\s*MOVES|CAUGHT\s+IN|OBTAINED\s+IN|REMOTELY\s+CAUGHT|^LOCATION$/i;

/**
 * Read the moveset under the GYMS & RAIDS tab: rows of "name …… power",
 * fast move first (Pokémon GO's fixed order), charged moves after. The
 * WEATHER BONUS sub-row and everything past the buddy/provenance section is
 * skipped. Rows are reconstructed from block geometry (name left, power
 * right, same visual row).
 */
export function readMoves(blocks: OcrTextBlock[]): MyPokemonMove[] {
  const tab = blocks.find((b) => /GYMS\s*&\s*RAIDS/i.test(b.text || ""));
  if (!tab) return [];
  const startY = tab.y + tab.h;
  // The section ends at the first structural block below the tab.
  let endY = 1;
  for (const b of blocks) {
    if (b.y <= startY) continue;
    if (MOVE_SECTION_END.test((b.text || "").trim())) {
      endY = Math.min(endY, b.y);
    }
  }

  const inSection = blocks.filter((b) => {
    const cy = b.y + b.h / 2;
    return cy > startY && cy < endY;
  });

  // Group into visual rows by y-centre.
  const rows: OcrTextBlock[][] = [];
  const sorted = [...inSection].sort((a, b) => a.y + a.h / 2 - (b.y + b.h / 2));
  for (const b of sorted) {
    const cy = b.y + b.h / 2;
    const row = rows.find((r) => {
      const rcy = r[0].y + r[0].h / 2;
      return Math.abs(rcy - cy) < ROW_TOLERANCE;
    });
    if (row) row.push(b);
    else rows.push([b]);
  }

  const moves: MyPokemonMove[] = [];
  for (const row of rows) {
    const rowText = row.map((b) => b.text || "").join(" ");
    if (WEATHER_BONUS.test(rowText)) continue;
    if (/TRAINER\s*BATTLES/i.test(rowText)) continue;

    // Power: the rightmost numeric-only block on the row.
    let power: number | null = null;
    let powerBlock: OcrTextBlock | null = null;
    for (const b of [...row].sort((a, c) => c.x - a.x)) {
      const n = parseNumber((b.text || "").trim());
      if (n != null && n <= 400 && b.x > 0.6) {
        power = n;
        powerBlock = b;
        break;
      }
    }
    // Name: leftmost block with letters (a merged "Play Rough 90" block also
    // yields the trailing power).
    let name: string | null = null;
    for (const b of [...row].sort((a, c) => a.x - c.x)) {
      if (b === powerBlock) continue;
      const t = (b.text || "").trim();
      if (!/[A-Za-z]{3,}/.test(t)) continue;
      const merged = t.match(/^(.+?)\s+(\d{1,3})$/);
      if (merged && power == null) {
        name = merged[1].trim();
        power = parseNumber(merged[2]);
      } else {
        name = t;
      }
      break;
    }
    if (!name) continue;
    // Vision glues the power bar's edge onto the name as a dash ("Behemoth
    // Bash -", 2026-09-04 owner screenshots), which failed the Game Master
    // name gate below and lost the charged move. Trailing punctuation is
    // never part of a move name.
    name = name.replace(/[\s\-–—·.:,]+$/, "");
    // The type icon at the row's left edge comes back as a bullet or a stray
    // glyph glued to the front of the name ("• Incinerate", 2026-09-16 owner
    // screenshots): a move name starts with a letter. A trailing "+" is KEPT:
    // it is part of the name of a Mega-only move ("Dark Pulse+").
    name = name.replace(/^[^A-Za-z]+/, "");
    // ML Kit reads that same icon as a LETTER ("O Tackle", "O Power Whip":
    // 2026-10-26 Galaxy S21+ logcat, the Normal and Grass icons alike), which
    // the strip above leaves alone and the Game Master gate below then
    // rejects, so no Android scan ever stored a move and the HUD never had
    // pills to draw. A move name never starts with a one- or two-character
    // word, so when the text is not a move, drop one such leading token and
    // look again; "Oir Slash" (three characters) stays unknown and is dropped.
    if (!isKnownMoveName(name)) {
      const glyph = name.match(/^\S{1,2}\s+(.+)$/);
      if (glyph && isKnownMoveName(glyph[1])) name = glyph[1];
    }
    if (name.length < 3 || name.length > 24) continue;
    // Page chrome, never a move — matched CASE-SENSITIVELY against the page's
    // ALL-CAPS chrome so the real Title Case move "Weather Ball" survives.
    // Prefix-tolerant because the HUD's own overlay clips rows mid-word
    // ("WEATHER BONU" leaked into a Psyduck record, 2026-08-20 device pass).
    if (/WEATHER|BONUS|NEW ATT|POWER UP/.test(name)) continue;
    // OUR OWN HUD floats over the moves list, and OCR reads it too: its
    // headline ("CRAMORANT #845"), status line ("• My Pokémon updated") and
    // fact slots ("LVL 45.5") have all landed as moves on device. The
    // categorical lock: a move name must exist in the shipped Game Master
    // move list (same corpus the type chips use) — anything else is chrome,
    // an echo, or an OCR mangle, and none of those belong on a record.
    if (!isKnownMoveName(name)) continue;
    // Store the Game Master's spelling, not the page's: GO prints "Future
    // Sight" for the corpus's "Futuresight", and the detail page matches a
    // record's moves to its rows by name (moveNameKey). An ambiguous prefix
    // ("Weather Ball" for the typed variants) keeps the printed name.
    name = canonicalMoveName(name) ?? name;
    // Vision sometimes returns the same line twice at a jittered y, which
    // lands the same move in two row groups ("Bug Bite · Bug Bite").
    if (moves.some((m) => moveNameKey(m.name) === moveNameKey(name!))) continue;
    moves.push({ name, power, kind: "fast" }); // kind fixed below
    if (moves.length >= 4) break; // a page shows at most 1 fast + 3 charged
  }
  // Pokémon GO lists the fast move first, then the charged moves — and a
  // Pokémon HAS exactly one fast move and at most two charged. Anything past
  // that shape is a mis-grouped row, so it is dropped rather than kept.
  return moves
    .map((m, i) => ({ ...m, kind: (i === 0 ? "fast" : "charged") as "fast" | "charged" }))
    .filter((m, i) => i < 3);
}

/**
 * Parse a printed d/m/y-or-m/d/y date to ISO. Only when unambiguous (one
 * segment > 12) do we commit an order; both-ambiguous dates fall back to
 * day-first — Pokémon GO prints the device locale and this app's user base is
 * day-first — with the raw string always preserved alongside.
 */
export function parsePrintedDate(raw: string): string | null {
  const m = (raw || "").trim().match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/);
  if (!m) return null;
  let a = Number(m[1]);
  let b = Number(m[2]);
  const year = Number(m[3]);
  if (!Number.isFinite(a) || !Number.isFinite(b) || !Number.isFinite(year)) {
    return null;
  }
  if (a > 12 && b > 12) return null;
  // Day-first unless the first segment can only be a month position value.
  let day: number;
  let month: number;
  if (a > 12) {
    day = a;
    month = b;
  } else if (b > 12) {
    day = b;
    month = a;
  } else {
    day = a;
    month = b;
  }
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const mm = String(month).padStart(2, "0");
  const dd = String(day).padStart(2, "0");
  return `${year}-${mm}-${dd}`;
}

const PROVENANCE_LABELS: Array<[RegExp, ProvenanceKind]> = [
  [/CAUGHT\s+IN\s+THE\s+WILD/i, "wild"],
  [/REMOTELY\s+CAUGHT\s+AT\s+A\s+RAID|CAUGHT\s+AT\s+A\s+RAID/i, "raid"],
  [/OBTAINED\s+IN\s+A\s+TRADE/i, "trade"],
  [/HATCHED/i, "egg"],
  [/^LOCATION$/i, "location"],
];

const DATE_ONLY = /^\d{1,2}[/.-]\d{1,2}[/.-]\d{4}$/;

/**
 * Read the provenance card: each entry is an UPPERCASE label with its value
 * (a place or trainer name) below it, optionally followed by a date line. A
 * traded Pokémon shows several entries (trade + original catch + current
 * location), so this is a list, never a single fact.
 */
export function readProvenance(
  blocks: OcrTextBlock[],
): MyPokemonProvenanceEntry[] {
  const out: MyPokemonProvenanceEntry[] = [];
  for (const b of blocks) {
    const t = (b.text || "").trim();
    const hit = PROVENANCE_LABELS.find(([re]) => re.test(t));
    if (!hit) continue;
    const [, kind] = hit;
    const lcy = b.y + b.h / 2;
    // Value: the nearest non-date text block below the label, left-aligned
    // with it.
    let value: string | null = null;
    let valueCy = lcy;
    let bestDy = 0.08;
    for (const v of blocks) {
      if (v === b) continue;
      const vt = (v.text || "").trim();
      if (!vt || DATE_ONLY.test(vt)) continue;
      if (PROVENANCE_LABELS.some(([re]) => re.test(vt))) continue;
      if (!/[A-Za-z]{2,}/.test(vt)) continue;
      const dy = v.y + v.h / 2 - lcy;
      if (dy <= 0 || dy >= bestDy) continue;
      if (Math.abs(v.x - b.x) > 0.2) continue;
      value = vt;
      valueCy = v.y + v.h / 2;
      bestDy = dy;
    }
    // Date: nearest date-only block below the value.
    let dateRaw: string | null = null;
    let bestDateDy = 0.07;
    for (const d of blocks) {
      const dt = (d.text || "").trim();
      if (!DATE_ONLY.test(dt)) continue;
      const dy = d.y + d.h / 2 - valueCy;
      if (dy <= 0 || dy >= bestDateDy) continue;
      if (Math.abs(d.x - b.x) > 0.25) continue;
      dateRaw = dt;
      bestDateDy = dy;
    }
    out.push({
      kind,
      label: t.toUpperCase(),
      value,
      dateRaw,
      dateIso: dateRaw ? parsePrintedDate(dateRaw) : null,
    });
  }
  return out;
}

export interface AppraisalSheetRead {
  speciesName: string;
  dateRaw: string;
  dateIso: string | null;
  location: string;
}

/**
 * The appraisal bottom sheet — "This Slaking was caught on 11/06/2021 around
 * Mascot, NSW, Australia." — names the SPECIES even when the Pokémon is
 * nicknamed, so it outranks the title as a species signal. The sentence often
 * splits across two OCR blocks, so the lower-page blocks are joined in
 * reading order before matching.
 */
export function readAppraisalSheet(
  blocks: OcrTextBlock[],
): AppraisalSheetRead | null {
  const lower = blocks
    .filter((b) => b.y > 0.72)
    .sort((a, b) => a.y - b.y || a.x - b.x);
  const joined = lower
    .map((b) => (b.text || "").trim())
    .filter(Boolean)
    .join(" ");
  const m = joined.match(APPRAISAL_SENTENCE);
  if (m) {
    const dateRaw = m[2].trim();
    return {
      speciesName: m[1].trim(),
      dateRaw,
      dateIso: parsePrintedDate(dateRaw),
      location: m[3].trim().replace(/\.$/, ""),
    };
  }
  // Clipped sheet (our own HUD floats over its middle): the species name
  // still reads — take it and leave date/location for the detail view.
  const prefix = joined.match(APPRAISAL_SENTENCE_PREFIX);
  if (prefix) {
    return { speciesName: prefix[1].trim(), dateRaw: "", dateIso: null, location: "" };
  }
  return null;
}

// ── Appraisal IV bars (pixel sampling) ───────────────────────────────────────

export interface AppraisalLabelGeometry {
  attack: OcrTextBlock;
  defense: OcrTextBlock;
  hp: OcrTextBlock;
}

export function findAppraisalLabels(
  blocks: OcrTextBlock[],
): AppraisalLabelGeometry | null {
  const attack = blocks.find((b) => isAppraisalStatLabel(b, "Attack"));
  const defense = blocks.find((b) => isAppraisalStatLabel(b, "Defense"));
  const hp = blocks.find(
    (b) =>
      (b.text || "").trim() === "HP" &&
      b.x < 0.55 &&
      b.y > (defense ? defense.y : 0.4),
  );
  if (!attack || !defense || !hp) return null;
  // Sanity: labels must stack top-to-bottom.
  if (!(attack.y < defense.y && defense.y < hp.y)) return null;
  return { attack, defense, hp };
}

/** The strip ROI holding one stat label's bar (label-anchored; see the
 * geometry note above the IV_* constants). */
export function ivBarStripRoi(label: OcrTextBlock): OcrRegion {
  const x = Math.max(0, label.x - IV_STRIP_LEFT_PAD);
  return {
    x,
    y: Math.min(1, label.y + label.h + IV_STRIP_TOP_GAP),
    w: Math.min(IV_STRIP_W, 1 - x),
    h: IV_STRIP_H,
  };
}

/**
 * Saturated warm bar FILL (orange mid-fill, red 0/15 nub, gradient edges),
 * judged against `ref`, the card white directly above this column.
 *
 * 2026-09-17 owner frames (Pichu, Clamperl): with the HUD parked bottom-right
 * the PiP window's drop shadow dims the whole appraisal panel, deeper towards
 * the card (card white 243 instead of 255, the empty grey 198-214 instead of
 * 226). Absolute thresholds then lost the dim tail of the fill and most of
 * the empty run, the best row slid to the bar's pale highlight, and the same
 * bar read one lower (HP 12 became 11, Defense 10 became 9). Each triple is
 * its own record, so one Pokémon showed two league picks. Everything here is
 * a RATIO to the local white, which the shadow scales the same way.
 */
function isIvBarFill(r: number, g: number, b: number, ref: number): boolean {
  return r >= 0.7 * ref && r - b >= 0.137 * ref && r >= g - 10 && b < 0.79 * ref;
}

/**
 * The TRUE bar-empty grey (~R226 G225 B230 under a 255 white, so 0.80-0.95
 * of the local white), deliberately tighter than the shared ocrPixelOps band:
 * the translucent card's tinted white IS the reference, so it never counts,
 * or the bar's measured extent bleeds into the card.
 */
function isIvBarEmpty(r: number, g: number, b: number, ref: number): boolean {
  const mx = Math.max(r, g, b);
  const mn = Math.min(r, g, b);
  const sat = mx > 0 ? (mx - mn) / mx : 0;
  return sat < 0.06 && mx >= 0.78 * ref && mx <= 0.955 * ref;
}

/** Rows at the top of a strip that are card white (the gap under the label). */
const IV_STRIP_REF_ROWS = 4;
/** Half-window over which a column's white reference is the local maximum. */
const IV_STRIP_REF_WINDOW = 8;

/**
 * Per-column card white, read from the strip's own top rows (the gap between
 * the label and the bar), then a windowed maximum so a stray ink or artefact
 * pixel never darkens a column's reference. A strip with no white up top (a
 * synthetic fixture, a crop that missed) falls back to a flat 255, which is
 * the pre-2026-09-17 absolute behaviour.
 */
function ivStripWhiteReference(
  data: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
): number[] {
  const rows = Math.min(IV_STRIP_REF_ROWS, height);
  const col = new Array<number>(width).fill(0);
  for (let x = 0; x < width; x++) {
    let best = 0;
    for (let y = 0; y < rows; y++) {
      const i = (y * width + x) * 4;
      const mx = Math.max(data[i], data[i + 1], data[i + 2]);
      if (mx > best) best = mx;
    }
    col[x] = best;
  }
  const ref = new Array<number>(width).fill(255);
  let seen = 0;
  for (let x = 0; x < width; x++) {
    let best = 0;
    for (let k = Math.max(0, x - IV_STRIP_REF_WINDOW); k <= Math.min(width - 1, x + IV_STRIP_REF_WINDOW); k++) {
      if (col[k] > best) best = col[k];
    }
    if (best >= 150) {
      ref[x] = best;
      seen += 1;
    }
  }
  return seen >= width / 2 ? ref : new Array<number>(width).fill(255);
}

export interface IvBarStripRead {
  /** 0-15. */
  value: number;
  /** 0-100: how much of the expected bar the strip actually showed. */
  confidence: number;
  /** Raw fill fraction over the FIXED bar length (diagnostics). */
  fillRatio: number;
}

/**
 * Measure one IV bar from its label-anchored strip.
 *
 * The denominator is the KNOWN bar length (IV_BAR_LEN_FRAC of the frame
 * width), never a pixel-derived extent — fill pixels only exist inside the
 * real bar, so `fill / knownLength` is exact even when stray grey pixels sit
 * beside the bar. `barLenPx` is that known length expressed in this strip's
 * pixel scale; rows are scored by bar-pixel density and the best row ±2 are
 * averaged.
 */
export function measureIvBarStrip(
  data: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
  barLenPx: number,
  diag?: string[],
): IvBarStripRead | null {
  if (width < 40 || height < 4 || barLenPx < 30) {
    diag?.push(`${width}x${height} bar${Math.round(barLenPx)} too-small`);
    return null;
  }
  // Row bar-pixel densities.
  let bestRow = -1;
  let bestScore = 0;
  const rowFill = new Array<number>(height).fill(0);
  const rowBar = new Array<number>(height).fill(0);
  const ref = ivStripWhiteReference(data, width, height);
  for (let y = 0; y < height; y++) {
    let fill = 0;
    let bar = 0;
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4;
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      if (isIvBarFill(r, g, b, ref[x])) {
        fill += 1;
        bar += 1;
      } else if (isIvBarEmpty(r, g, b, ref[x])) {
        bar += 1;
      }
    }
    rowFill[y] = fill;
    rowBar[y] = bar;
    if (bar > bestScore) {
      bestScore = bar;
      bestRow = y;
    }
  }
  if (diag) {
    const mid = [...ref].sort((a, b) => a - b)[Math.floor(ref.length / 2)];
    diag.push(
      `${width}x${height} bar${Math.round(barLenPx)} best=${bestScore}@${bestRow}/need${Math.ceil(barLenPx * IV_BAR_MIN_COVERAGE)} fill=${bestRow >= 0 ? rowFill[bestRow] : 0} ref=${mid}`,
    );
  }
  if (bestRow < 0 || bestScore < barLenPx * IV_BAR_MIN_COVERAGE) return null;
  // Average the best row ±2 (rows that still look like the bar).
  let fillSum = 0;
  let rows = 0;
  let coverage = 0;
  for (let y = Math.max(0, bestRow - 2); y <= Math.min(height - 1, bestRow + 2); y++) {
    if (rowBar[y] < barLenPx * IV_BAR_MIN_COVERAGE) continue;
    fillSum += rowFill[y];
    coverage += rowBar[y];
    rows += 1;
  }
  if (rows === 0) return null;
  const fillRatio = Math.min(1, fillSum / rows / barLenPx);
  const value = Math.max(0, Math.min(15, Math.round(fillRatio * 15)));
  const confidence = Math.max(
    0,
    Math.min(100, Math.round((coverage / rows / barLenPx) * 100)),
  );
  return { value, confidence, fillRatio: Math.round(fillRatio * 1000) / 1000 };
}

/**
 * Star tier derived from the IV total — Pokémon GO's own mapping (3★ 37-45,
 * 2★ 30-36, 1★ 23-29, 0★ below). Derivation replaced the seal pixel-read on
 * 2026-08-20: the seal's ORANGE medallion ring reads as gold whether or not
 * any star is earned, so the counter reported 1★ on a 0★ Growlithe; the IVs
 * are already read, and the mapping is exact.
 */
// Lives in myPokemonDerived so the CSV import path doesn't drag this whole
// OCR chunk (and its bundled fingerprint JSON) into the main bundle.
export { starsFromIv };

// ── OCR orchestration (browser) ──────────────────────────────────────────────

export interface MyPokemonOcrDeps {
  loadImage: (dataUrl: string) => Promise<HTMLImageElement>;
  ocrBlocks: (img: HTMLImageElement, roi: OcrRegion) => Promise<OcrTextBlock[]>;
  cropRgba: (
    img: HTMLImageElement,
    roi: OcrRegion,
  ) => { data: Uint8ClampedArray; width: number; height: number } | null;
}

/**
 * Read an individual caught-Pokémon page (either view) into a structured
 * frame. The browser-only primitives are injected so all parsing and geometry
 * logic unit-tests in Node — the same seam `pokemonDetailDexOcr` uses.
 */
export async function extractMyPokemonPage(
  dataUrl: string,
  deps: MyPokemonOcrDeps = defaultDeps(),
): Promise<MyPokemonFrame> {
  const warnings: string[] = [];
  const empty: MyPokemonFrame = {
    isMyPokemonPage: false,
    view: "detail",
    displayName: null,
    speciesId: null,
    speciesName: null,
    nickname: null,
    cp: null,
    hpCurrent: null,
    hpMax: null,
    gender: null,
    weightKg: null,
    heightM: null,
    sizeClass: null,
    types: [],
    stardust: null,
    candy: null,
    candyXl: null,
    candyFamilyName: null,
    megaEnergy: null,
    megaEnergySpeciesName: null,
    megaEnergies: [],
    megaReads: [],
    megaRestMs: null,
    isLucky: false,
    isShiny: false,
    isShadow: false,
    isDynamax: false,
    isGmax: false,
    maxMoves: null,
    tags: [],
    moves: [],
    provenance: [],
    caughtDateRaw: null,
    caughtDateIso: null,
    caughtLocation: null,
    iv: null,
    appraisalStars: null,
    confidence: 0,
    warnings,
  };
  if (!dataUrl) return empty;

  let img: HTMLImageElement;
  try {
    img = await deps.loadImage(dataUrl);
  } catch {
    warnings.push("image_decode_failed");
    return empty;
  }

  const rawBlocks = await deps.ocrBlocks(img, SCAN_ROI);
  const blocks = dropHudEcho(dropStatusBarChrome(rawBlocks.map((b) => mapBlockToFrame(b, SCAN_ROI))));

  if (!looksLikeMyPokemonPage(blocks)) {
    if (looksLikeMegaLevelPage(blocks)) {
      return { ...empty, isMegaLevelPage: true, megaLevelPage: readMegaLevelPage(blocks) };
    }
    if (looksLikeStorageBox(blocks)) return { ...empty, isStorageBox: true };
    return { ...empty, encounter: readEncounterBanner(blocks) };
  }

  return parseMyPokemonBlocks(blocks, {
    cropRgba: (roi) => deps.cropRgba(img, roi),
    warnings,
    ivBarLenFrac: ivBarLengthFrac(img.naturalWidth || img.width, img.naturalHeight || img.height),
  });
}

/**
 * Pure block→frame parse, split from `extractMyPokemonPage` so unit tests can
 * feed synthetic block sets (derived from real device screenshots) plus a
 * fake pixel sampler without any DOM.
 */
export function parseMyPokemonBlocks(
  blocks: OcrTextBlock[],
  ctx: {
    cropRgba: (
      roi: OcrRegion,
    ) => { data: Uint8ClampedArray; width: number; height: number } | null;
    warnings: string[];
    /** IV bar length for the frame's shape; the phone bar when omitted. */
    ivBarLenFrac?: number;
  },
): MyPokemonFrame {
  const { warnings } = ctx;

  const cp = readCp(blocks);
  const hp = readHp(blocks);
  const displayName = hp ? readDisplayName(blocks, hp.block) : null;
  const isLucky = blocks.some((b) => LUCKY_BANNER.test(b.text || ""));
  // The Max band (2026-09-02 owner report: an uploaded Dynamax Gyarados came
  // back with the flag off). Read straight off the printed label — this is a
  // fact about the individual, not something inferred from the species, and
  // the MEGA EVOLVE button deliberately proves nothing: it is offered on every
  // mega-capable species whether or not this one has ever been evolved.
  const maxBand = readMaxBand(blocks, hp?.block ?? null);
  const isGmax = maxBand === "gmax";
  let isDynamax = maxBand === "dynamax";
  // The Max Moves ribbon and tiles (2026-09-04). A Pokémon with Max Moves
  // in the Dynamax roster IS a Dynamax one (its band may sit off-screen);
  // Crowned Zacian / Zamazenta and Eternatus have Max Moves without being
  // in that roster, so the ribbon alone never ticks the Dynamax dex for them.
  const maxMoves = readMaxMoves(blocks, hp?.block ?? null);
  const sizeRead = readWeightHeight(blocks);
  const types = readTypes(blocks);
  const resources = readResources(blocks);
  // The Mega Level circles and the rest badge (2026-09-03): the detail view
  // only, since the appraisal overlay covers the buttons.
  const megaReads = readMegaLevelCircles(blocks, ctx.cropRgba);
  const megaRestMs = readMegaRest(blocks);
  const tags = hp ? readTags(blocks, hp.block) : [];
  const gender = readGender(blocks);
  const moves = readMoves(blocks);
  const provenance = readProvenance(blocks);
  const sheet = readAppraisalSheet(blocks);

  // Appraisal overlay (read BEFORE the species, which the Nidoran rule
  // below settles from these bars): pixel-sample the three IV bars from their own
  // label-anchored strips (see the geometry note above the IV_* constants).
  let iv: MyPokemonIvRead | null = null;
  let appraisalStars: number | null = null;
  let ivDiag: string | undefined;
  const labels = findAppraisalLabels(blocks);
  const view: MyPokemonFrame["view"] = labels || sheet ? "appraisal" : "detail";
  if (labels) {
    const stripDiag: string[] = [];
    const readBar = (key: string, label: OcrTextBlock): IvBarStripRead | null => {
      const roi = ivBarStripRoi(label);
      const rgba = ctx.cropRgba(roi);
      if (!rgba) {
        stripDiag.push(`${key} no-crop`);
        return null;
      }
      // The known bar length, expressed in this strip's pixel scale.
      const barLenPx = ((ctx.ivBarLenFrac ?? IV_BAR_LEN_FRAC) / roi.w) * rgba.width;
      const line: string[] = [];
      const read = measureIvBarStrip(rgba.data, rgba.width, rgba.height, barLenPx, line);
      stripDiag.push(`${key} ${line.join(" ")}${read ? ` =${read.value}` : " =null"}`);
      return read;
    };
    const atk = readBar("atk", labels.attack);
    const def = readBar("def", labels.defense);
    const hpBar = readBar("hp", labels.hp);
    if (atk && def && hpBar) {
      iv = {
        atk: atk.value,
        def: def.value,
        hp: hpBar.value,
        confidence: Math.min(atk.confidence, def.confidence, hpBar.confidence),
      };
      appraisalStars = starsFromIv(iv);
      // The per-bar fill and reference on EVERY read (2026-09-28 accuracy
      // sweep): a successful read used to log nothing about the bars, so a
      // device that reads one step low could not be diagnosed from its
      // exported scan log. The probe line carries this string.
      ivDiag = `bars ${stripDiag.join(" | ")}`;
    } else {
      warnings.push("iv_bars_not_found");
      const f = (n: number) => n.toFixed(3);
      const lab = (b: OcrTextBlock) => `${f(b.x)},${f(b.y)} ${f(b.w)}x${f(b.h)}`;
      ivDiag = `labels atk[${lab(labels.attack)}] def[${lab(labels.defense)}] hp[${lab(labels.hp)}] | ${stripDiag.join(" | ")}`;
    }
  } else if (sheet) {
    // The sheet says the overlay is up but no Attack / Defense / HP label
    // was read: show what OCR DID put in the panel's corner instead.
    const corner = blocks
      .filter((b) => b.x < 0.55 && b.y > 0.6 && b.y < 0.92)
      .sort((a, b) => a.y - b.y)
      .slice(0, 8)
      .map((b) => `"${(b.text || "").trim()}"@${b.x.toFixed(2)},${b.y.toFixed(2)}`);
    ivDiag = `labels none | corner ${corner.join(" ") || "(empty)"}`;
  }

  // Species: the appraisal sheet names the species even on a nicknamed
  // Pokémon, so it wins; the display name is the fallback signal.
  const sheetSpeciesId = sheet
    ? speciesIdFromDisplayName(sheet.speciesName)
    : null;
  const nameSpeciesId = speciesIdFromDisplayName(displayName);
  // Third resolver, for nicknamed Pokémon whose sheet our HUD covers (the
  // "Zamaz 15 15 15 100" Zamazenta, 2026-08-20 device log): the candy label
  // names the FAMILY BASE, and when that family has NO evolutions the base is
  // the only stage this Pokémon can be. Exact for every single-stage species —
  // which covers most legendaries, the most-nicknamed Pokémon there are.
  let candySpeciesId: number | null = null;
  if (resources.candyFamilyName) {
    const baseId = speciesIdFromDisplayName(resources.candyFamilyName);
    if (baseId != null && evolutionChainFor(baseId).length === 0) {
      candySpeciesId = baseId;
    }
  }
  // Fourth resolver, weakest signal: an IV-STYLED display name
  // ("Rayquaza¹⁵¹⁴¹⁵", or "Rayquaza151415" as OCR flattens it) names its own
  // species once the stat decorations are stripped. Last because the sheet,
  // an exact name and the candy rule are all stronger claims.
  const styledSpeciesId = speciesIdFromStyledName(displayName);
  // Fifth resolver, Nidoran only: the name is the same word for #29 and #32
  // and the glyph that tells them apart is one OCR often drops.
  const nidoranId =
    isBareNidoran(sheet?.speciesName ?? null) || isBareNidoran(displayName)
      ? nidoranIdFromStats({ gender, cp, hp: hp?.max ?? null, iv })
      : null;
  const speciesId =
    sheetSpeciesId ?? nameSpeciesId ?? candySpeciesId ?? styledSpeciesId ?? nidoranId;
  // A height or weight the species could never print is an OCR mangle, not a
  // Pokémon (2026-09-16 owner report: a "2.36m" Houndoom stored as 36 m).
  // Judged against the species' own average once the species is known.
  const weightKg =
    sizeRead.weightKg != null &&
    (speciesId == null || isPlausibleWeight(speciesId, null, sizeRead.weightKg))
      ? sizeRead.weightKg
      : null;
  if (sizeRead.weightKg != null && weightKg == null) warnings.push("implausible_weight");
  const heightM =
    sizeRead.heightM != null &&
    (speciesId == null || isPlausibleHeight(speciesId, null, sizeRead.heightM))
      ? sizeRead.heightM
      : null;
  if (sizeRead.heightM != null && heightM == null) warnings.push("implausible_height");
  const sizeClass = sizeRead.sizeClass;
  const speciesName = speciesId != null ? SPECIES_NAMES[speciesId] ?? null : null;
  if (
    !isDynamax &&
    !isGmax &&
    maxMoves?.hasMaxMoves &&
    speciesId != null &&
    speciesUsesMaxMoves(speciesId) &&
    variantSupportsSpecies("dynamax", speciesId)
  ) {
    isDynamax = true;
  }
  // Keyed on the EXACT match: an IV-styled name resolves the species above
  // but is still the trainer's real nickname, so it stays stored as one.
  // A printed name that is the resolved species name misread (one glyph
  // off: "Clamperı" for Clamperl; truncated by a mid-render frame: "Spriga:"
  // for Sprigatito; 2026-09-17 owner frames) is not a nickname. The species
  // it is checked against is the one this frame resolved (the sheet's "This
  // X was caught…" line first, GO's own statement even on a renamed
  // Pokémon), so a real nickname that merely resembles some OTHER species
  // is untouched.
  const resolvedSpeciesName =
    sheet?.speciesName ?? (speciesId != null ? (SPECIES_NAMES[speciesId] ?? null) : null);
  const misreadSpeciesName =
    displayName != null &&
    nameSpeciesId == null &&
    looksLikeSpeciesMisread(displayName, resolvedSpeciesName);
  const nickname =
    displayName != null &&
    nameSpeciesId == null &&
    !misreadSpeciesName &&
    // "Nidoran" short of its glyph is the species name, never a nickname.
    !isBareNidoran(displayName)
      ? displayName
      : null;
  if (displayName != null && speciesId == null) warnings.push("species_unresolved");

  // Shiny: fingerprint the sprite crop against the species' two palettes.
  // Only claimed when the classifier is sure; a null verdict leaves the flag
  // false and notes why, so a shiny miss is visible in the device log.
  // DETAIL view only, and only when the top-anchored fields were read (the
  // sprite is actually on screen): the appraisal view stands the team leader
  // in this exact crop, and a leader's outfit colour must never become a
  // shiny verdict; a deep-scrolled page has mid-page cards there instead.
  let isShiny = false;
  if (speciesId != null && view === "detail" && (cp != null || hp != null)) {
    const spriteRgba = ctx.cropRgba(DETAIL_SPRITE_ROI.roi);
    if (spriteRgba) {
      const verdict = classifyShinySprite(
        spriteRgba.data,
        spriteRgba.width,
        spriteRgba.height,
        speciesId,
      );
      if (verdict.shiny === true) isShiny = true;
      else if (verdict.shiny === null) warnings.push(`shiny_${verdict.reason}`);
    }
  }
  // Shadow: the purple flame aura GO draws around the model, read off the
  // same crop's outer margins (2026-09-18). Both views: on the appraisal the
  // leader stands below this band. Only ever asserts; a record already
  // marked Shadow by the trainer keeps it whatever a frame says.
  let isShadow = false;
  if (speciesId != null && (cp != null || hp != null)) {
    // Knowledge before pixels (2026-09-20 device pass: Clodsire, Toxapex,
    // Mareanie and Dragalge all read as Shadow). The POISON habitat backdrop
    // is the storm sky's twin: hue 240-260 at 0.47-0.53 value on four device
    // frames, against the storm sky's 0.44-0.48, and its violet swirl covers
    // 6-20% of the crop, inside the flames' band. No threshold separates
    // them, so a poison-type page is read as not Shadow (owner direction:
    // likely not, rather than wrong most of the time), and a species that
    // has no Shadow form is never Shadow whatever the backdrop. A Shadow
    // poison Pokémon can still be marked by hand on its record page.
    // The species table names the typing, never the read (2026-09-20 owner
    // direction): a one-pill read must not decide whether the page is poison.
    const pageTypes = typesFor(speciesId);
    if (!isVariantAvailable("shadow", speciesId)) {
      warnings.push("shadow_species_never");
    } else if (pageTypes.includes("poison")) {
      warnings.push("shadow_poison_backdrop");
    } else {
      const spriteRgba = ctx.cropRgba(DETAIL_SPRITE_ROI.roi);
      if (spriteRgba) {
        isShadow = classifyShadowAura(spriteRgba.data, spriteRgba.width, spriteRgba.height).shadow;
      }
    }
  }

  // Caught date/location: the appraisal sheet is authoritative; the
  // provenance card's original-catch entry is the detail-view source.
  const catchEntry =
    provenance.find((p) => p.kind === "wild") ??
    provenance.find((p) => p.kind === "raid") ??
    provenance.find((p) => p.kind === "egg") ??
    null;
  // `||` not `??`: a clipped sheet reads species-only and carries EMPTY
  // date/location strings, which must fall through to the provenance card.
  const caughtDateRaw = sheet?.dateRaw || catchEntry?.dateRaw || null;
  const caughtDateIso = sheet?.dateIso ?? catchEntry?.dateIso ?? null;
  const caughtLocation = sheet?.location || catchEntry?.value || null;
  if (caughtDateRaw && !caughtDateIso) warnings.push("caught_date_ambiguous");

  // Confidence: how much of the page's identity core was actually read.
  let score = 0;
  if (speciesId != null) score += 35;
  else if (displayName != null) score += 15;
  if (cp != null) score += 20;
  if (hp != null) score += 20;
  if (iv != null) score += 15;
  if (moves.length > 0) score += 10;
  const confidence = Math.min(100, score);

  return {
    isMyPokemonPage: true,
    view,
    displayName,
    speciesId,
    speciesName,
    nickname,
    cp,
    hpCurrent: hp?.current ?? null,
    hpMax: hp?.max ?? null,
    gender,
    weightKg,
    heightM,
    sizeClass,
    types,
    stardust: resources.stardust,
    candy: resources.candy,
    candyXl: resources.candyXl,
    candyFamilyName: resources.candyFamilyName,
    megaEnergy: resources.megaEnergy,
    megaEnergySpeciesName: resources.megaEnergySpeciesName,
    megaEnergies: resources.megaEnergies,
    megaReads,
    megaRestMs,
    isLucky,
    isShiny,
    isShadow,
    isDynamax,
    isGmax,
    maxMoves,
    tags,
    moves,
    provenance,
    caughtDateRaw,
    caughtDateIso,
    caughtLocation,
    iv,
    appraisalStars,
    confidence,
    warnings,
    ivDiag,
  };
}

// ── Browser-default deps ─────────────────────────────────────────────────────

function mapBlockToFrame(b: OcrTextBlock, roi: OcrRegion): OcrTextBlock {
  return {
    ...b,
    x: roi.x + b.x * roi.w,
    y: roi.y + b.y * roi.h,
    w: b.w * roi.w,
    h: b.h * roi.h,
  };
}

function defaultDeps(): MyPokemonOcrDeps {
  return {
    loadImage: loadImageFromDataUrl,
    ocrBlocks: async (img, roi) => {
      const [res] = await recognizeRegions(img, [roi], { multiline: true });
      return res?.blocks ?? [];
    },
    cropRgba: cropRgbaFromImage,
  };
}

function loadImageFromDataUrl(dataUrl: string): Promise<HTMLImageElement> {
  // One decode per frame, shared by every consumer (lib/frameImageCache).
  return loadFrameImage(dataUrl);
}

function cropRgbaFromImage(
  img: HTMLImageElement,
  roi: OcrRegion,
): { data: Uint8ClampedArray; width: number; height: number } | null {
  const W = img.naturalWidth || img.width;
  const H = img.naturalHeight || img.height;
  const x = Math.max(0, Math.floor(roi.x * W));
  const y = Math.max(0, Math.floor(roi.y * H));
  const w = Math.max(1, Math.min(W - x, Math.floor(roi.w * W)));
  const h = Math.max(1, Math.min(H - y, Math.floor(roi.h * H)));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d", CANVAS_READBACK);
  if (!ctx) return null;
  ctx.drawImage(img, x, y, w, h, 0, 0, w, h);
  const d = ctx.getImageData(0, 0, w, h);
  return { data: d.data, width: w, height: h };
}
